"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { Upload, Loader2, FileText, CheckCircle, ClipboardList, X, Download, FileOutput, FileJson, Brain, PenTool, ChevronRight, ChevronLeft, User, FileUp, Ruler, MessageSquare, ListChecks, Trash2, Zap, BrainCircuit, Wand2, Eye, Plus } from "lucide-react";
import { jsPDF } from "jspdf";
import { saveAs } from "file-saver";
import { cn } from "@/lib/utils";
import { extractTextFromFile, detectNames, type ExtractionResult } from "@/lib/text-extractor";
import { anonymizeText, deanonymizeText, buildDeanonymizationMap } from "@/lib/anonymizer";
import { useBilanProcessLog, type StepUsage } from "@/lib/bilan-process-logger";
import { isConclusionBuggy as isConclusionBuggyShared, CONCLUSION_LENGTH_OPTIONS, CONCLUSION_LENGTH_LABELS, CONCLUSION_LENGTH_CUSTOM_LABEL, CONCLUSION_LENGTH_CUSTOM_MIN, CONCLUSION_LENGTH_CUSTOM_MAX, type ConclusionLength, type ConclusionLengthChoice } from "@/lib/bilan-conclusion";
import { generateCorrespondenceTable, DOCUMENT_CONTEXT_MAX_LENGTH, type PatientResult } from "@/lib/identify-and-anonymize";
import { toast } from "sonner";
import { DISCOVERY_PHRASES } from "@/config/discovery-phrases";
import { LottieLoading } from "./lottie-loading";


/** Appel fetch + parse JSON. Si le serveur renvoie du HTML (erreur 404/500), lance une erreur claire. */
async function fetchJson<T = unknown>(url: string, options?: RequestInit): Promise<{ res: Response; data: T }> {
  const res = await fetch(url, options);
  const text = await res.text();
  let data: T;
  try {
    data = (text ? JSON.parse(text) : null) as T;
  } catch (e) {
    if (typeof text === "string" && text.trimStart().startsWith("<"))
      throw new Error("Le serveur a renvoyé une page d'erreur (HTML) au lieu de JSON. Vérifiez l'onglet Réseau (F12) ou les logs serveur.");
    throw e;
  }
  return { res, data };
}

function parseDeanonymMap(raw: string | null | undefined): Record<string, string> | null {
  if (!raw || typeof raw !== "string") return null;
  try {
    const o = JSON.parse(raw) as Record<string, string>;
    return o && typeof o === "object" ? o : null;
  } catch {
    return null;
  }
}

const PATIENT_CODE_REGEX = /\[([A-Z0-9]+-\d{2})\]/g;
function buildFallbackDeanonymMapForNotes(notesExcerpt: string, patientDisplayName: string): Record<string, string> | null {
  const codes = new Set<string>();
  let m: RegExpExecArray | null;
  PATIENT_CODE_REGEX.lastIndex = 0;
  while ((m = PATIENT_CODE_REGEX.exec(notesExcerpt)) !== null) codes.add(`[${m[1]}]`);
  if (codes.size === 0) return null;
  const map: Record<string, string> = {};
  codes.forEach((code) => { map[code] = patientDisplayName; });
  return map;
}

interface PatientIdentification {
  results: PatientResult[];
}

interface QuestionAnswer {
  selected: string;
  confidence: "high" | "medium" | "low";
  sourceText: string;
}

interface CategoryAnswers {
  category: string;
  answers: Record<string, QuestionAnswer>;
  availableOptions: Record<string, string[]>;
}

interface QuestionnaireData {
  selectedCategories: string[];
  categoryAnswers: Record<string, CategoryAnswers>;
}

interface BirthdateInfo {
  found: boolean;
  birthdate: string | null;
  age: number | null;
}

interface AnonymizationResult {
  anonymizedText: string;
  replacements: { type: string; original: string; replacement: string; count: number }[];
}

interface OrthopticAnalysis {
  success: boolean;
  analysis: string;
  metadata: Record<string, unknown>;
}

interface BilanLogUser {
  id?: string;
  name: string;
  email: string;
  image?: string | null;
  role?: string;
}

// Les phrases de découverte sont importées depuis @/config/discovery-phrases.ts

export interface BilanGeneratorProps {
  defaultOrthoptistName?: string;
  practitioners?: string[];
  user?: BilanLogUser | null;
}

export function BilanGenerator({
  defaultOrthoptistName = "",
  practitioners = [],
  user = null,
}: BilanGeneratorProps) {
  const [inputMode, setInputMode] = useState<"file" | "text">("file");
  const [pastedText, setPastedText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [orthoptistName, setOrthoptistName] = useState(defaultOrthoptistName);
  const [rehabilitationCare, setRehabilitationCare] = useState(true);
  const [contextNotes, setContextNotes] = useState("");
  // Modif: force "optionA" (Gemini 2.5) par défaut pour plus de réflexion
  // NOTE: On utilise maintenant le "dual mode" donc ce state sert de default pour la selection
  const [reasoningModel, setReasoningModel] = useState<"optionB" | "optionA">("optionA");
  const [conclusionLength, setConclusionLength] = useState<ConclusionLengthChoice>("moyen");
  const [conclusionLengthCustom, setConclusionLengthCustom] = useState<number>(200);
  const [conclusionLengthConfig, setConclusionLengthConfig] = useState<Record<ConclusionLength, number> | null>(null);
  useEffect(() => {
    fetch("/api/config/conclusion-lengths")
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data && typeof data.court === "number" && typeof data.moyen === "number" && typeof data.long === "number") {
          setConclusionLengthConfig({ court: data.court, moyen: data.moyen, long: data.long });
        }
      })
      .catch(() => { });
  }, []);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<"idle" | "phase1" | "questionnaire_modal" | "phase2" | "done">("idle");
  const [patientName, setPatientName] = useState("");
  const [patientBirthDate, setPatientBirthDate] = useState("");
  const [patientsList, setPatientsList] = useState<{ id: string; firstName: string; lastName: string }[]>([]);
  const [patientsListLoading, setPatientsListLoading] = useState(false);
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);

  // Doctors / Correspondants
  const [doctorsList, setDoctorsList] = useState<{ id: string; firstName: string; lastName: string; title?: string; city?: string }[]>([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState<string>("");
  const [userSignature, setUserSignature] = useState<string>("");
  const [userCabinet, setUserCabinet] = useState<string>("");
  const [userRpps, setUserRpps] = useState<string>("");
  const [userAddress, setUserAddress] = useState<string>("");

  // Add Doctor Modal
  const [showAddDoctorModal, setShowAddDoctorModal] = useState(false);
  const [isCreatingDoctor, setIsCreatingDoctor] = useState(false);
  const [newDoctorForm, setNewDoctorForm] = useState({
    title: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    zipCode: "",
  });

  useEffect(() => {
    // Charger les médecins
    fetch("/api/me/doctors").then(r => r.ok ? r.json() : null).then(data => {
      if (data?.doctors) setDoctorsList(data.doctors);
    });
    // Charger le profil pour signature
    // Charger le profil pour signature
    fetch("/api/me/profile", { cache: "no-store" }).then(r => r.ok ? r.json() : null).then(data => {
      console.log("Profile data loaded (no-cache):", data);
      if (data?.profile?.signature) setUserSignature(data.profile.signature);
      if (data?.profile?.cabinetName) setUserCabinet(data.profile.cabinetName);
      if (data?.profile?.rpps) setUserRpps(data.profile.rpps);

      // Construction de l'adresse
      const parts = [];
      if (data?.profile?.addressLine1) parts.push(data.profile.addressLine1);
      if (data?.profile?.addressLine2) parts.push(data.profile.addressLine2);
      if (data?.profile?.postalCode || data?.profile?.city) {
        parts.push(`${data.profile.postalCode || ""} ${data.profile.city || ""}`.trim());
      }
      if (parts.length > 0) {
        const fullAddress = parts.join(", ");
        console.log("Setting user address:", fullAddress);
        setUserAddress(fullAddress);
      }
    });
  }, []);
  const [extractionResult, setExtractionResult] = useState<ExtractionResult | null>(null);
  const [identifiedPatient, setIdentifiedPatient] = useState<PatientIdentification | null>(null);
  const [patientFromDbResult, setPatientFromDbResult] = useState<{
    found: boolean;
    notesExcerpt?: string;
    notesAnonymizationMap?: string | null;
    patientDisplayName?: string;
    declensions?: string[];
    declensionsCount?: number;
  } | null>(null);
  const [patientHistoryDismissed, setPatientHistoryDismissed] = useState(false);
  const [anonymizationResult, setAnonymizationResult] = useState<AnonymizationResult | null>(null);
  const [birthdateInfo, setBirthdateInfo] = useState<BirthdateInfo | null>(null);
  const [questionnaireData, setQuestionnaireData] = useState<QuestionnaireData | null>(null);
  const [showQuestionnaireModal, setShowQuestionnaireModal] = useState(false);
  const [orthopticAnalysis, setOrthopticAnalysis] = useState<OrthopticAnalysis | null>(null);
  // DUAL ANALYSIS STATE
  const [analysisResults, setAnalysisResults] = useState<{ optionA: OrthopticAnalysis | null, optionB: OrthopticAnalysis | null }>({ optionA: null, optionB: null });
  const [analysisLoading, setAnalysisLoading] = useState<{ optionA: boolean, optionB: boolean }>({ optionA: false, optionB: false });
  // A/B TESTING CONCLUSION STATE
  const [conclusionResultsDuel, setConclusionResultsDuel] = useState<{ optionA: string | null, optionB: string | null }>({ optionA: null, optionB: null });
  const [conclusionLoading, setConclusionLoading] = useState<{ optionA: boolean, optionB: boolean }>({ optionA: false, optionB: false });
  const [analysisErrors, setAnalysisErrors] = useState<{ optionA: string | null, optionB: string | null }>({ optionA: null, optionB: null });

  const [selectedAnalysis, setSelectedAnalysis] = useState<"optionA" | "optionB" | "both">("optionB");
  // const [orthopticAnalysis, setOrthopticAnalysis] = useState<OrthopticAnalysis | null>(null); // Kept for compat, synced with selected

  const [conclusionResult, setConclusionResult] = useState<{ raw: string; final: string | null } | null>(null);
  /** Conclusion éditable par l'utilisateur avant téléchargement (initialisée à conclusionResult.final) */
  const [editedConclusion, setEditedConclusion] = useState("");
  const [validatingQuestionnaire, setValidatingQuestionnaire] = useState(false);

  // State pour l'auto-save
  const [savedReportId, setSavedReportId] = useState<string | null>(null);
  const [savedPatientId, setSavedPatientId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isRegeneratingQuestionnaire, setIsRegeneratingQuestionnaire] = useState(false);
  const [backgroundPhase, setBackgroundPhase] = useState<"idle" | "running" | "ready" | "error">("idle");
  const [backgroundProgress, setBackgroundProgress] = useState("");
  const [step, setStep] = useState(1);
  const [formModified, setFormModified] = useState(false);
  const [analysisStage, setAnalysisStage] = useState<"idle" | "reasoning" | "conclusion">("idle");
  const [analysisElapsedSeconds, setAnalysisElapsedSeconds] = useState(0);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [showNoQuotaModal, setShowNoQuotaModal] = useState(false);
  const [isVisualizing, setIsVisualizing] = useState(false);
  const [isDownloadingWord, setIsDownloadingWord] = useState(false);

  const processLog = useBilanProcessLog();
  const practitionersList = orthoptistName ? [orthoptistName, ...practitioners.filter((p) => p !== orthoptistName)] : practitioners;
  const wasProcessingRef = useRef(false);
  const backgroundRunIdRef = useRef(0);
  const geminiStartedRef = useRef(false);
  const conclusionStartedRef = useRef(false);
  const fullPhase2OnStep6Ref = useRef(false);
  const patientStepModifiedRef = useRef(false);
  const phase2RunIdRef = useRef(0);
  const analysisTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const patientDropdownBlurRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reformulationQueueRef = useRef<Promise<void>>(Promise.resolve());


  useEffect(() => {
    if (user) processLog.setUser({ id: user.id, name: user.name, email: user.email, image: user.image });
  }, [user?.id, user?.name, user?.email]);

  useEffect(() => {
    if (conclusionResult?.final != null) setEditedConclusion(conclusionResult.final);
  }, [conclusionResult?.final]);

  useEffect(() => {
    if (wasProcessingRef.current && phase === "done" && processLog.hasSteps) processLog.saveLog();
    wasProcessingRef.current = phase === "phase1" || phase === "phase2";
  }, [phase, processLog.hasSteps]);

  // Chrono d'avancement : démarre au début de l'analyse (~40 s), puis conclusion (~20 s)
  useEffect(() => {
    if (analysisStage === "idle") {
      if (analysisTimerRef.current) {
        clearInterval(analysisTimerRef.current);
        analysisTimerRef.current = null;
      }
      setAnalysisElapsedSeconds(0);
      return;
    }
    setAnalysisElapsedSeconds(0);
    analysisTimerRef.current = setInterval(() => {
      setAnalysisElapsedSeconds((s) => s + 1);
    }, 1000);
    return () => {
      if (analysisTimerRef.current) {
        clearInterval(analysisTimerRef.current);
        analysisTimerRef.current = null;
      }
    };
  }, [analysisStage]);

  // Rotation des phrases de découverte toutes les 10 secondes pendant l'analyse
  useEffect(() => {
    if (analysisStage === "idle") {
      setPhraseIndex(0);
      return;
    }
    // Randomiser le point de départ quand l'analyse commence
    setPhraseIndex(Math.floor(Math.random() * DISCOVERY_PHRASES.length));

    const interval = setInterval(() => {
      setPhraseIndex((prev) => (prev + 1) % DISCOVERY_PHRASES.length);
    }, 45000);
    return () => clearInterval(interval);
  }, [analysisStage]);

  const updateQuestionnaireAnswer = useCallback((category: string, question: string, newValue: string) => {
    console.log('[Questionnaire] Updating answer:', { category, question, newValue });
    setFormModified(true);
    setQuestionnaireData((prev) => {
      if (!prev) return prev;
      const updated = { ...prev.categoryAnswers };
      if (updated[category]?.answers[question]) {
        updated[category] = {
          ...updated[category],
          answers: {
            ...updated[category].answers,
            [question]: { ...updated[category].answers[question], selected: newValue, confidence: "high" as const },
          },
        };
      }
      console.log('[Questionnaire] Updated data:', { categoryAnswers: updated });
      return { ...prev, categoryAnswers: updated };
    });
  }, []);

  const runPhase1 = useCallback(async (selectedFile: File, background = false) => {
    const runId = background ? backgroundRunIdRef.current : 0;
    setError(null);
    processLog.reset(selectedFile.name);
    if (background) {
      setBackgroundPhase("running");
      setBackgroundProgress("Extraction du texte...");
    } else {
      setPhase("phase1");
      setProgress("Extraction du texte...");
    }

    const setProg = (msg: string) => {
      if (background) setBackgroundProgress(msg);
      else setProgress(msg);
    };

    try {
      processLog.startStep("extraction", "Extraction du texte", { fileName: selectedFile.name, type: selectedFile.type, sizeBytes: selectedFile.size });
      const extraction = await extractTextFromFile(selectedFile);
      processLog.endStep("extraction", {
        linesCount: extraction.lines.length,
        namesCount: extraction.names.length,
        textLength: extraction.text.length,
        textSentToNextSteps: extraction.text,
      });
      setExtractionResult(extraction);
      setProg("Identification du patient...");

      if (extraction.names.length === 0) {
        if (background && runId !== backgroundRunIdRef.current) return;
        setError("Aucun nom détecté dans le document.");
        if (background) setBackgroundPhase("error");
        else setPhase("idle");
        return;
      }

      processLog.startStep("identify-patient", "Identification du patient (IA)", {
        detectedNamesCount: extraction.names.length,
        documentContextLength: extraction.text.substring(0, DOCUMENT_CONTEXT_MAX_LENGTH).length,
      });
      const { res: identifyRes, data: identifyData } = await fetchJson<{ results?: unknown; error?: string; _usage?: unknown; _promptSent?: unknown }>("/api/identify-patient", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          detectedNames: extraction.names,
          practitioners: practitionersList,
          practiceAddress: "",
          documentContext: extraction.text.substring(0, DOCUMENT_CONTEXT_MAX_LENGTH),
        }),
      });
      processLog.endStep("identify-patient", identifyData as Record<string, unknown>, identifyData._usage as StepUsage | undefined, undefined, { inputPrompt: identifyData._promptSent as string | undefined });
      if (!identifyRes.ok || !identifyData.results) {
        if (background && runId !== backgroundRunIdRef.current) return;
        setError((identifyData as { error?: string }).error || "Erreur identification patient");
        if (background) setBackgroundPhase("error");
        else setPhase("idle");
        return;
      }

      const resultsWithIds = generateCorrespondenceTable((identifyData.results ?? []) as { format: string; value: string }[]);
      setIdentifiedPatient({ results: resultsWithIds });
      const nameParts = (identifyData.results as { value?: string }[]).map((r) => r.value?.trim()).filter(Boolean) as string[];
      setPatientName(nameParts.slice(0, 2).join(" "));

      const searchName = nameParts.slice(0, 2).join(" ");
      processLog.startStep("fetch-patient-db", "Recherche patient en base", { searchName });
      let patientFromDb: { firstName?: string; lastName?: string; nameDeclensions?: string[] | null; notes?: string | null; notesAnonymizationMap?: string | null } | null = null;
      try {
        if (searchName) {
          const { res: listRes, data: listData } = await fetchJson<{ patients?: { id: string; firstName?: string; lastName?: string }[] }>(`/api/patients?${new URLSearchParams({ name: searchName })}`);
          if (listRes.ok && Array.isArray(listData.patients) && listData.patients.length > 0) {
            const first = listData.patients[0];
            const { res: detailRes, data: detail } = await fetchJson<{ id?: string; firstName?: string; lastName?: string; nameDeclensions?: string[] | string; notes?: string; notesAnonymizationMap?: string | null }>(`/api/patients/${first.id}`);
            if (detailRes.ok && detail?.id) {
              const decl = Array.isArray(detail.nameDeclensions) ? detail.nameDeclensions : typeof detail.nameDeclensions === "string" ? (() => { try { return JSON.parse(detail.nameDeclensions || "[]"); } catch { return []; } })() : [];
              const notesExcerpt = detail.notes ? String(detail.notes).substring(0, 800) : undefined;
              const patientNameFromDb = detail.lastName && detail.firstName ? `${detail.lastName} ${detail.firstName}` : undefined;
              patientFromDb = { firstName: detail.firstName, lastName: detail.lastName, nameDeclensions: decl, notes: detail.notes, notesAnonymizationMap: detail.notesAnonymizationMap ?? null };
              processLog.endStep("fetch-patient-db", {
                found: true,
                searchName,
                patientId: detail.id,
                patientName: patientNameFromDb,
                summaryLength: detail.notes?.length ?? 0,
                notesExcerpt,
                declensionsCount: decl?.length ?? 0,
                declensions: decl,
              });
            } else {
              processLog.endStep("fetch-patient-db", { found: false, searchName, declensionsCount: 0 });
            }
          } else {
            processLog.endStep("fetch-patient-db", { found: false, searchName, declensionsCount: 0 });
          }
        } else {
          processLog.endStep("fetch-patient-db", { found: false, searchName: "", reason: "Aucun nom à rechercher" });
        }
      } catch (_e) {
        processLog.endStep("fetch-patient-db", undefined, undefined, { message: _e instanceof Error ? _e.message : String(_e) });
        setPatientFromDbResult({ found: false });
      }
      setPatientFromDbResult({
        found: !!patientFromDb,
        notesExcerpt: patientFromDb?.notes ? String(patientFromDb.notes).substring(0, 800) : undefined,
        notesAnonymizationMap: patientFromDb?.notesAnonymizationMap ?? undefined,
        patientDisplayName: patientFromDb?.lastName != null && patientFromDb?.firstName != null ? `${patientFromDb.lastName} ${patientFromDb.firstName}` : undefined,
        declensions: patientFromDb?.nameDeclensions ?? undefined,
        declensionsCount: patientFromDb?.nameDeclensions?.length ?? 0,
      });
      setPatientHistoryDismissed(false);

      setProg("Anonymisation...");
      const baseCorrespondence = resultsWithIds
        .filter((r) => r.value && r.anonymousId)
        .map((r) => ({ original: r.value, anonymousId: r.anonymousId! }));
      const firstCode = baseCorrespondence[0]?.anonymousId;
      const existing = new Set(baseCorrespondence.map((c) => c.original.toLowerCase()));
      const declEntries = firstCode && patientFromDb?.nameDeclensions
        ? patientFromDb.nameDeclensions
          .map((d) => (typeof d === "string" ? d.trim() : ""))
          .filter((d) => d && !existing.has(d.toLowerCase()))
          .map((original) => ({ original, anonymousId: firstCode }))
        : [];
      const correspondence = [...baseCorrespondence, ...declEntries];
      const anonymized = anonymizeText(extraction.text, correspondence, practitionersList);
      processLog.startStep("anonymization", "Anonymisation", { textLength: extraction.text.length, correspondenceCount: correspondence.length });
      processLog.endStep("anonymization", {
        anonymizedLength: anonymized.anonymizedText.length,
        replacementsCount: anonymized.replacements?.length ?? 0,
        anonymizedTextReceived: anonymized.anonymizedText,
      });
      setAnonymizationResult(anonymized);

      setProg("Date de naissance...");
      processLog.startStep("extract-birthdate", "Extraction date de naissance", { anonymizedText: anonymized.anonymizedText });
      const { data: birthData } = await fetchJson<{ birthdate?: string; age?: number; found?: boolean; _usage?: unknown; _promptSent?: unknown }>("/api/extract-birthdate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ anonymizedText: anonymized.anonymizedText }),
      });
      processLog.endStep("extract-birthdate", birthData as Record<string, unknown>, birthData._usage as StepUsage | undefined, undefined, { inputPrompt: birthData._promptSent as string | undefined });
      setBirthdateInfo({ found: birthData?.found ?? false, birthdate: birthData?.birthdate ?? null, age: birthData?.age ?? null });
      if (birthData?.birthdate) {
        const b = String(birthData.birthdate).trim();
        if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(b)) {
          const [d, m, y] = b.split("/");
          setPatientBirthDate(`${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`);
        } else if (/^\d{4}-\d{2}-\d{2}$/.test(b)) {
          setPatientBirthDate(b);
        }
      }

      setProg("Questionnaire (données cliniques)...");
      processLog.startStep("questionnaire-single", "Questionnaire - extraction complète", { anonymizedText: anonymized.anonymizedText, singlePrompt: true });
      const { data: questData } = await fetchJson<{ selectedCategories?: string[]; categoryAnswers?: Record<string, CategoryAnswers>; _usage?: unknown; _promptSent?: unknown }>("/api/analyze-questionnaire", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ anonymizedText: anonymized.anonymizedText, singlePrompt: true }),
      });
      processLog.endStep("questionnaire-single", questData as Record<string, unknown>, questData._usage as StepUsage | undefined, undefined, { inputPrompt: questData._promptSent as string | undefined });
      const selectedCategories: string[] = questData.selectedCategories || [];
      const categoryAnswers: Record<string, CategoryAnswers> = questData.categoryAnswers || {};
      setQuestionnaireData({ selectedCategories, categoryAnswers });
      if (background) {
        if (runId !== backgroundRunIdRef.current) return;
        setBackgroundProgress("");
        setBackgroundPhase("ready");
      } else {
        setProgress("");
        setPhase("questionnaire_modal");
        setShowQuestionnaireModal(true);
      }
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      if (background && runId !== backgroundRunIdRef.current) return;
      setError(e.message.includes("is not valid JSON") ? "Le serveur a renvoyé une page d'erreur (HTML) au lieu de JSON. Vérifiez l'onglet Réseau (F12) ou les logs serveur." : e.message);
      if (background) setBackgroundPhase("error");
      else setPhase("idle");
    }
  }, [practitionersList, processLog]);

  /** Traite le texte collé directement (saute l'extraction de fichier) */
  const runPhase1FromText = useCallback(async (text: string, background = false) => {
    const runId = background ? backgroundRunIdRef.current : 0;
    setError(null);
    processLog.reset("Texte collé");
    if (background) {
      setBackgroundPhase("running");
      setBackgroundProgress("Traitement du texte...");
    } else {
      setPhase("phase1");
      setProgress("Traitement du texte...");
    }

    const setProg = (msg: string) => {
      if (background) setBackgroundProgress(msg);
      else setProgress(msg);
    };

    try {
      // Créer un résultat d'extraction à partir du texte collé (même logique que extractTextFromFile)
      processLog.startStep("text-input", "Texte collé par l'utilisateur", { textLength: text.length });
      const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
      const names = detectNames(text, lines);
      const extraction: ExtractionResult = {
        text: text,
        lines: lines,
        names: names,
      };
      processLog.endStep("text-input", {
        linesCount: lines.length,
        namesCount: names.length,
        textLength: text.length,
        textSentToNextSteps: text,
      });
      setExtractionResult(extraction);
      setProg("Identification du patient...");

      if (extraction.names.length === 0) {
        if (background && runId !== backgroundRunIdRef.current) return;
        setError("Aucun nom détecté dans le texte. Vérifiez que le texte contient bien des noms de patients.");
        if (background) setBackgroundPhase("error");
        else setPhase("idle");
        return;
      }

      processLog.startStep("identify-patient", "Identification du patient (IA)", {
        detectedNamesCount: extraction.names.length,
        documentContextLength: text.substring(0, DOCUMENT_CONTEXT_MAX_LENGTH).length,
      });
      const { res: identifyRes, data: identifyData } = await fetchJson<{ results?: unknown; error?: string; _usage?: unknown; _promptSent?: unknown }>("/api/identify-patient", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          detectedNames: extraction.names,
          practitioners: practitionersList,
          practiceAddress: "",
          documentContext: text.substring(0, DOCUMENT_CONTEXT_MAX_LENGTH),
        }),
      });
      processLog.endStep("identify-patient", identifyData as Record<string, unknown>, identifyData._usage as StepUsage | undefined, undefined, { inputPrompt: identifyData._promptSent as string | undefined });
      if (!identifyRes.ok || !identifyData.results) {
        if (background && runId !== backgroundRunIdRef.current) return;
        setError((identifyData as { error?: string }).error || "Erreur identification patient");
        if (background) setBackgroundPhase("error");
        else setPhase("idle");
        return;
      }

      const resultsWithIds = generateCorrespondenceTable((identifyData.results ?? []) as { format: string; value: string }[]);
      setIdentifiedPatient({ results: resultsWithIds });
      const nameParts = (identifyData.results as { value?: string }[]).map((r) => r.value?.trim()).filter(Boolean) as string[];
      setPatientName(nameParts.slice(0, 2).join(" "));

      const searchName = nameParts.slice(0, 2).join(" ");
      processLog.startStep("fetch-patient-db", "Recherche patient en base", { searchName });
      let patientFromDb: { firstName?: string; lastName?: string; nameDeclensions?: string[] | null; notes?: string | null; notesAnonymizationMap?: string | null } | null = null;
      try {
        if (searchName) {
          const { res: listRes, data: listData } = await fetchJson<{ patients?: { id: string; firstName?: string; lastName?: string }[] }>(`/api/patients?${new URLSearchParams({ name: searchName })}`);
          if (listRes.ok && Array.isArray(listData.patients) && listData.patients.length > 0) {
            const first = listData.patients[0];
            const { res: detailRes, data: detail } = await fetchJson<{ id?: string; firstName?: string; lastName?: string; nameDeclensions?: string[] | string; notes?: string; notesAnonymizationMap?: string | null }>(`/api/patients/${first.id}`);
            if (detailRes.ok && detail?.id) {
              const decl = Array.isArray(detail.nameDeclensions) ? detail.nameDeclensions : typeof detail.nameDeclensions === "string" ? (() => { try { return JSON.parse(detail.nameDeclensions || "[]"); } catch { return []; } })() : [];
              const notesExcerpt = detail.notes ? String(detail.notes).substring(0, 800) : undefined;
              const patientNameFromDb = detail.lastName && detail.firstName ? `${detail.lastName} ${detail.firstName}` : undefined;
              patientFromDb = { firstName: detail.firstName, lastName: detail.lastName, nameDeclensions: decl, notes: detail.notes, notesAnonymizationMap: detail.notesAnonymizationMap ?? null };
              processLog.endStep("fetch-patient-db", {
                found: true,
                searchName,
                patientId: detail.id,
                patientName: patientNameFromDb,
                summaryLength: detail.notes?.length ?? 0,
                notesExcerpt,
                declensionsCount: decl?.length ?? 0,
                declensions: decl,
              });
            } else {
              processLog.endStep("fetch-patient-db", { found: false, searchName, declensionsCount: 0 });
            }
          } else {
            processLog.endStep("fetch-patient-db", { found: false, searchName, declensionsCount: 0 });
          }
        } else {
          processLog.endStep("fetch-patient-db", { found: false, searchName: "", reason: "Aucun nom à rechercher" });
        }
      } catch (_e) {
        processLog.endStep("fetch-patient-db", undefined, undefined, { message: _e instanceof Error ? _e.message : String(_e) });
        setPatientFromDbResult({ found: false });
      }
      setPatientFromDbResult({
        found: !!patientFromDb,
        notesExcerpt: patientFromDb?.notes ? String(patientFromDb.notes).substring(0, 800) : undefined,
        notesAnonymizationMap: patientFromDb?.notesAnonymizationMap ?? undefined,
        patientDisplayName: patientFromDb?.lastName != null && patientFromDb?.firstName != null ? `${patientFromDb.lastName} ${patientFromDb.firstName}` : undefined,
        declensions: patientFromDb?.nameDeclensions ?? undefined,
        declensionsCount: patientFromDb?.nameDeclensions?.length ?? 0,
      });
      setPatientHistoryDismissed(false);

      setProg("Anonymisation...");
      const baseCorrespondence = resultsWithIds
        .filter((r) => r.value && r.anonymousId)
        .map((r) => ({ original: r.value, anonymousId: r.anonymousId! }));
      const firstCode = baseCorrespondence[0]?.anonymousId;
      const existing = new Set(baseCorrespondence.map((c) => c.original.toLowerCase()));
      const declEntries = firstCode && patientFromDb?.nameDeclensions
        ? patientFromDb.nameDeclensions
          .map((d) => (typeof d === "string" ? d.trim() : ""))
          .filter((d) => d && !existing.has(d.toLowerCase()))
          .map((original) => ({ original, anonymousId: firstCode }))
        : [];
      const correspondence = [...baseCorrespondence, ...declEntries];
      const anonymized = anonymizeText(text, correspondence, practitionersList);
      processLog.startStep("anonymization", "Anonymisation", { textLength: text.length, correspondenceCount: correspondence.length });
      processLog.endStep("anonymization", {
        anonymizedLength: anonymized.anonymizedText.length,
        replacementsCount: anonymized.replacements?.length ?? 0,
        anonymizedTextReceived: anonymized.anonymizedText,
      });
      setAnonymizationResult(anonymized);

      setProg("Date de naissance...");
      processLog.startStep("extract-birthdate", "Extraction date de naissance", { anonymizedText: anonymized.anonymizedText });
      const { data: birthData } = await fetchJson<{ birthdate?: string; age?: number; found?: boolean; _usage?: unknown; _promptSent?: unknown }>("/api/extract-birthdate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ anonymizedText: anonymized.anonymizedText }),
      });
      processLog.endStep("extract-birthdate", birthData as Record<string, unknown>, birthData._usage as StepUsage | undefined, undefined, { inputPrompt: birthData._promptSent as string | undefined });
      setBirthdateInfo({ found: birthData?.found ?? false, birthdate: birthData?.birthdate ?? null, age: birthData?.age ?? null });
      if (birthData?.birthdate) {
        const b = String(birthData.birthdate).trim();
        if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(b)) {
          const [d, m, y] = b.split("/");
          setPatientBirthDate(`${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`);
        } else if (/^\d{4}-\d{2}-\d{2}$/.test(b)) {
          setPatientBirthDate(b);
        }
      }

      setProg("Questionnaire (données cliniques)...");
      processLog.startStep("questionnaire-single", "Questionnaire - extraction complète", { anonymizedText: anonymized.anonymizedText, singlePrompt: true });
      const { data: questData } = await fetchJson<{ selectedCategories?: string[]; categoryAnswers?: Record<string, CategoryAnswers>; _usage?: unknown; _promptSent?: unknown }>("/api/analyze-questionnaire", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ anonymizedText: anonymized.anonymizedText, singlePrompt: true }),
      });
      processLog.endStep("questionnaire-single", questData as Record<string, unknown>, questData._usage as StepUsage | undefined, undefined, { inputPrompt: questData._promptSent as string | undefined });
      const selectedCategories: string[] = questData.selectedCategories || [];
      const categoryAnswers: Record<string, CategoryAnswers> = questData.categoryAnswers || {};
      setQuestionnaireData({ selectedCategories, categoryAnswers });
      if (background) {
        if (runId !== backgroundRunIdRef.current) return;
        setBackgroundProgress("");
        setBackgroundPhase("ready");
      } else {
        setProgress("");
        setPhase("questionnaire_modal");
        setShowQuestionnaireModal(true);
      }
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      if (background && runId !== backgroundRunIdRef.current) return;
      setError(e.message.includes("is not valid JSON") ? "Le serveur a renvoyé une page d'erreur (HTML) au lieu de JSON. Vérifiez l'onglet Réseau (F12) ou les logs serveur." : e.message);
      if (background) setBackgroundPhase("error");
      else setPhase("idle");
    }
  }, [practitionersList, processLog]);

  /** Relance uniquement l'extraction du questionnaire (quand aucune donnée clinique n'a été extraite). */
  const regenerateQuestionnaire = useCallback(async () => {
    if (!anonymizationResult) return;
    setIsRegeneratingQuestionnaire(true);
    setQuestionnaireData(null); // Clear previous to give feedback
    try {
      processLog.startStep("questionnaire-single", "Questionnaire - extraction complète (regénération)", { anonymizedText: anonymizationResult.anonymizedText, singlePrompt: true });
      const { data: questData } = await fetchJson<{ selectedCategories?: string[]; categoryAnswers?: Record<string, CategoryAnswers>; _usage?: unknown; _promptSent?: unknown }>("/api/analyze-questionnaire", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ anonymizedText: anonymizationResult.anonymizedText, singlePrompt: true }),
      });
      processLog.endStep("questionnaire-single", questData as Record<string, unknown>, questData._usage as StepUsage | undefined, undefined, { inputPrompt: questData._promptSent as string | undefined });
      const selectedCategories: string[] = questData.selectedCategories || [];
      const categoryAnswers: Record<string, CategoryAnswers> = questData.categoryAnswers || {};
      setQuestionnaireData({ selectedCategories, categoryAnswers });
      toast.success("Extraction relancée avec succès.");
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e.message.includes("is not valid JSON") ? "Le serveur a renvoyé une erreur (HTML). Vérifiez l'onglet Réseau (F12) ou les logs serveur." : e.message);
      toast.error("Échec de la relance : " + e.message);
    } finally {
      setIsRegeneratingQuestionnaire(false);
    }
  }, [anonymizationResult, processLog]);

  /** Handler pour créer un nouveau médecin */
  const handleCreateDoctor = useCallback(async () => {
    if (!newDoctorForm.firstName.trim() || !newDoctorForm.lastName.trim()) {
      toast.error("Le prénom et le nom sont requis");
      return;
    }

    setIsCreatingDoctor(true);
    try {
      const response = await fetch("/api/me/doctors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newDoctorForm),
      });

      if (!response.ok) {
        throw new Error("Erreur lors de la création du médecin");
      }

      const { doctor } = await response.json();

      // Ajouter le nouveau médecin à la liste
      setDoctorsList(prev => [doctor, ...prev]);

      // Sélectionner automatiquement le nouveau médecin
      setSelectedDoctorId(doctor.id);

      // Réinitialiser le formulaire et fermer le modal
      setNewDoctorForm({
        title: "",
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        zipCode: "",
      });
      setShowAddDoctorModal(false);

      toast.success("Médecin ajouté avec succès");
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      toast.error("Échec de l'ajout : " + e.message);
    } finally {
      setIsCreatingDoctor(false);
    }
  }, [newDoctorForm]);

  /** Handler pour fermer le modal et réinitialiser le formulaire */
  const handleCloseAddDoctorModal = useCallback(() => {
    setShowAddDoctorModal(false);
    setNewDoctorForm({
      title: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      zipCode: "",
    });
  }, []);

  useEffect(() => {
    // Traitement automatique en arrière-plan uniquement pour les fichiers
    if (inputMode === "file" && file && backgroundPhase === "idle") {
      runPhase1(file, true);
    }
    // Pour le texte collé, on attend que l'utilisateur clique sur "Suivant"
  }, [file, inputMode, backgroundPhase, runPhase1]);

  // Sauvegarde automatique du patient et du bilan en base de données
  const saveToDatabase = useCallback(async () => {
    console.log("Tentative auto-save...", {
      identifiedPatient: !!identifiedPatient,
      extractionResult: !!extractionResult,
      conclusionResult: !!conclusionResult?.final
    });

    if (!identifiedPatient) {
      console.warn("Auto-save annulé: identifiedPatient manquant");
      return;
    }
    // extractionResult n'est pas strictement nécessaire si le fichier est un upload, mais utile pour le contenu
    // On permet de continuer même si extractionResult est null (cas rare) -> on mettra une chaine vide

    if (!conclusionResult?.final) {
      console.warn("Auto-save annulé: conclusion manquante");
      return;
    }

    setIsSaving(true);
    try {
      // 1. Préparer les données du patient
      // On cherche les valeurs dans identifiedPatient (résultat de l'IA)
      const findVal = (fmt: string) => identifiedPatient.results.find(r => r.format === fmt || r.format === fmt.toUpperCase())?.value;

      const nameParts = patientName.trim().split(/\s+/);
      const pLastName = nameParts.length > 0 ? nameParts[0] : (findVal("NOM") || "INCONNU");
      const pFirstName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : (findVal("Prénom") || "");

      const pBirthDate = patientBirthDate || (birthdateInfo?.birthdate ? birthdateInfo.birthdate.split("/").reverse().join("-") : undefined);

      // --- NOUVEAU : Appel à l'extraction pour générer notes / synthèse comme sur la page Patient ---
      let extractedNotes: string | null = null;
      let extractedSummary: string | null = null;
      let extractedImprovements: string | null = null;
      let extractedActions: string | null = null;
      let extractedPathologies: { name: string; description: string | null }[] = [];

      if (anonymizationResult?.anonymizedText) {
        try {
          const { res: extractRes, data: extractData } = await fetchJson<{
            notes?: string;
            reportSummary?: string;
            reportImprovements?: string;
            reportActionsToCome?: string;
            pathologies?: { name: string; description: string | null }[];
          }>("/api/patients/extract-from-document", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ anonymizedText: anonymizationResult.anonymizedText }),
          });

          if (extractRes.ok) {
            extractedNotes = extractData.notes ?? null;
            extractedSummary = extractData.reportSummary ?? null;
            extractedImprovements = extractData.reportImprovements ?? null;
            extractedActions = extractData.reportActionsToCome ?? null;
            extractedPathologies = extractData.pathologies ?? [];
            console.log("Pathologies extraites (debug):", extractedPathologies);
          }
        } catch (e) {
          console.error("Erreur extraction notes/synthèse:", e);
        }
      }
      // --- Fin appel extraction ---

      // Création/Mise à jour du Patient
      const patientRes = await fetchJson<{ id: string } & PatientResult>("/api/patients", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: pFirstName,
          lastName: pLastName,
          birthDate: pBirthDate,
          nameDeclensions: identifiedPatient.results.map(r => r.value),
          notes: extractedNotes || conclusionResult.final, // Utilise la note extraite, sinon fallback sur conclusion
          notesAnonymizationMap: null,
        }),
      });

      if (!patientRes.res.ok || !patientRes.data.id) {
        throw new Error("Erreur lors de la sauvegarde du patient");
      }

      const pId = patientRes.data.id;
      setSavedPatientId(pId);

      // Sauvegarde des pathologies si trouvées
      if (extractedPathologies.length > 0) {
        try {
          for (const patho of extractedPathologies) {
            await fetchJson(`/api/patients/${pId}/pathologies`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                name: patho.name,
                description: patho.description ?? undefined
              }),
            });
          }
        } catch (e) {
          console.error("Erreur sauvegarde pathologies:", e);
        }
      }

      // 2. Créer le rapport (Bilan)
      const reportDate = new Date().toISOString().split("T")[0];
      const title = `Bilan orthoptique du ${new Date().toLocaleDateString("fr-FR")}`;

      const fullContent = (extractionResult?.text || "") + "\n\nCONCLUSION :\n" + (editedConclusion || conclusionResult.final);

      const reportPayload = {
        title,
        content: fullContent ?? undefined,
        summary: (extractedSummary || (editedConclusion || conclusionResult.final)) ?? undefined,
        improvements: extractedImprovements ?? undefined,
        actionsToCome: extractedActions ?? undefined,
        reportDate: reportDate ?? undefined,
        originalFileName: (file?.name || "texte_colle.txt") ?? undefined,
        anonymizationMap: anonymizationResult?.replacements ? buildDeanonymizationMap(anonymizationResult.replacements) : null,
      };

      console.log("Payload création rapport:", JSON.stringify(reportPayload, null, 2));

      const reportRes = await fetchJson<{ id: string }>(`/api/patients/${pId}/reports`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(reportPayload),
      });

      if (!reportRes.res.ok) {
        console.error("Erreur création rapport (data):", reportRes.data);
        const errData = reportRes.data as { error?: string; details?: unknown };
        if (errData?.details) console.error("Détails validation:", errData.details);
        throw new Error(errData?.error || "Erreur lors de la création du rapport");
      }

      if (reportRes.data.id) {
        setSavedReportId(reportRes.data.id);
        toast.success("Patient et bilan enregistrés");
      } else {
        console.error("Réponse inattendue création rapport:", reportRes);
        throw new Error("Pas d'ID rapport retourné");
      }

    } catch (err) {
      console.error("Erreur auto-save:", err);
      toast.error("Erreur lors de l'enregistrement automatique: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setIsSaving(false);
    }
  }, [identifiedPatient, patientName, patientBirthDate, birthdateInfo, extractionResult, conclusionResult, anonymizationResult, file, editedConclusion]);

  // Auto-save disabled to allow manual triggering
  /*
  // Déclencheur de l'auto-save quand le bilan est terminé
  useEffect(() => {
    if (phase === "done" && conclusionResult?.final && !savedReportId && !isSaving) {
      saveToDatabase();
    }
  }, [phase, conclusionResult, savedReportId, isSaving, saveToDatabase]);
  */

  // Analyse orthoptique Raisonnement (DUAL MODE: Parallel Flash + Pro)
  const runOrthopticAnalysisOnly = useCallback(async () => {
    if (!anonymizationResult || !questionnaireData || !identifiedPatient) return;
    console.log('[Analysis] Starting analysis with questionnaire data:', {
      categoryCount: Object.keys(questionnaireData.categoryAnswers).length,
      categoryAnswers: questionnaireData.categoryAnswers
    });
    const runId = ++phase2RunIdRef.current;
    setPhase("phase2");


    setAnalysisStage("reasoning");
    setAnalysisResults({ optionA: null, optionB: null });
    setAnalysisLoading({ optionA: true, optionB: true });
    setConclusionResultsDuel({ optionA: null, optionB: null });
    setConclusionLoading({ optionA: false, optionB: false }); // Sera true après analyse
    setAnalysisErrors({ optionA: null, optionB: null });
    setOrthopticAnalysis(null); // Reset global

    // Helper pour lancer une analyse ET sa conclusion
    const runOneChain = async (model: "optionB" | "optionA", reformulationProvider: "gemini" | "mistral") => {
      let orthoData: OrthopticAnalysis & { _promptSent?: unknown } | null = null;
      try {
        // --- ETAPE 1 : ANALYSE ORTHOPTIQUE (Parallèle) ---
        const orthoBody = {
          anonymizedText: anonymizationResult.anonymizedText,
          questionnaire: questionnaireData.categoryAnswers,
          patientAge: birthdateInfo?.age ?? null,
          birthdate: birthdateInfo?.birthdate ?? null,
          contextNotes,
          rehabilitationCare,
          reasoningModel: model,
          patientFromDb: patientFromDbResult?.found && !patientHistoryDismissed ? { notesExcerpt: patientFromDbResult.notesExcerpt, declensionsCount: patientFromDbResult.declensionsCount, declensions: patientFromDbResult.declensions } : undefined,
        };
        processLog.startStep(`analyze-orthoptic-${model}`, `Analyse orthoptique (${model === "optionA" ? "Option A: Gemini" : "Option B: GPT"})`, orthoBody);

        const response = await fetchJson<OrthopticAnalysis & { _promptSent?: unknown }>("/api/analyze-orthoptic", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(orthoBody)
        });
        orthoData = response.data;

        processLog.endStep(`analyze-orthoptic-${model}`, orthoData as unknown as Record<string, unknown>, undefined, undefined, { inputPrompt: orthoData._promptSent as string | undefined });

        if (runId !== phase2RunIdRef.current) return;

        setAnalysisResults((prev) => ({ ...prev, [model]: orthoData }));
        setAnalysisLoading((prev) => ({ ...prev, [model]: false }));

      } catch (err) {
        if (runId !== phase2RunIdRef.current) return;
        console.error(`Erreur analyse ${model}:`, err);
        setAnalysisErrors((prev) => ({ ...prev, [model]: "Erreur lors de l'analyse. Veuillez réessayer." }));
        setAnalysisLoading((prev) => ({ ...prev, [model]: false }));
        return; // Stop if analysis failed
      }

      // --- ETAPE 2 : GENERATION CONCLUSION (Séquentielle dans une file d'attente) ---
      // On s'ajoute à la fin de la file pour éviter de saturer Vertex AI
      reformulationQueueRef.current = reformulationQueueRef.current.then(async () => {
        if (runId !== phase2RunIdRef.current) return;
        if (!orthoData) return;

        try {
          setConclusionLoading((prev) => ({ ...prev, [model]: true }));

          const conclusionBody = {
            analysis: orthoData.analysis,
            patientAge: birthdateInfo?.age ?? null,
            patientCodes: identifiedPatient.results.filter((r) => r.anonymousId).map((r) => ({ format: r.format, code: r.anonymousId! })),
            isChild: (birthdateInfo?.age ?? 0) < 18,
            contextNotes,
            rehabilitationCare,
            signature: orthoptistName,
            analysisModel: model,
            reformulationProvider,
            ...(conclusionLength === "custom"
              ? { targetWords: Math.max(CONCLUSION_LENGTH_CUSTOM_MIN, Math.min(CONCLUSION_LENGTH_CUSTOM_MAX, conclusionLengthCustom)) }
              : { maxLength: conclusionLength }),
          };

          processLog.startStep(`generate-conclusion-${model}`, `Conclusion (${model})`, conclusionBody);

          let { res: conclRes, data: conclData } = await fetchJson<{ conclusion?: string; _promptSent?: unknown; code?: string }>("/api/generate-conclusion", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(conclusionBody)
          });

          if (conclRes.ok && isConclusionBuggyShared((conclData as { conclusion?: string }).conclusion)) {
            // Retry si bug (ex: JSON malformé ou vide)
            const retry = await fetchJson<{ conclusion?: string; _promptSent?: unknown }>("/api/generate-conclusion", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(conclusionBody) });
            conclData = retry.data;
          }

          processLog.endStep(`generate-conclusion-${model}`, conclData as Record<string, unknown>);

          if (runId !== phase2RunIdRef.current) return;

          const rawConclusion = ((conclData as { conclusion?: string }).conclusion ?? "") as string;

          // Desanonymisation locale pour l'affichage final
          let finalConclusion = rawConclusion;
          const formats = identifiedPatient.results.filter((r) => r.value && r.anonymousId);
          const firstValue = formats[0]?.value;
          for (const f of formats) {
            const re = new RegExp(`\\[${f.anonymousId}\\]`, "gi");
            finalConclusion = finalConclusion.replace(re, f.value);
          }
          if (firstValue) finalConclusion = finalConclusion.replace(/\[CODE\]/g, firstValue);

          setConclusionResultsDuel((prev) => ({ ...prev, [model]: finalConclusion }));

          // Log de la désanonymisation pour cet embranchement
          processLog.startStep(`deanonymize-${model}`, `Désanonymisation (${model})`, { rawConclusionLength: rawConclusion.length });
          processLog.endStep(`deanonymize-${model}`, { finalConclusion });


        } catch (err) {
          if (runId !== phase2RunIdRef.current) return;
          console.error(`Erreur conclusion ${model}:`, err);
          setAnalysisErrors((prev) => ({ ...prev, [model]: "Erreur lors de la génération de la conclusion. Veuillez réessayer." }));
        } finally {
          if (runId === phase2RunIdRef.current) {
            setConclusionLoading((prev) => ({ ...prev, [model]: false }));
          }
        }
      });
    };


    // Lance les deux chaines en parallèle
    // Option A (state "optionA") : Gemini 2.5 reasoning + Mistral reformulation
    // Option B (state "optionB") : 5.1-mini reasoning + Mistral reformulation
    await Promise.all([runOneChain("optionA", "mistral"), runOneChain("optionB", "mistral")]);

  }, [anonymizationResult, questionnaireData, identifiedPatient, birthdateInfo, contextNotes, rehabilitationCare, reasoningModel, patientFromDbResult, patientHistoryDismissed, processLog, conclusionLength, conclusionLengthCustom, orthoptistName]);

  // Conclusion Vertex seule (réformulation avec analyse existante)
  const runConclusionOnly = useCallback(async () => {
    // DUAL MODE: Déterminer quelle analyse utiliser
    let analysisToUse = orthopticAnalysis?.analysis;
    if (!analysisToUse) {
      if (selectedAnalysis === "optionA" && analysisResults.optionA) analysisToUse = analysisResults.optionA.analysis;
      else if (selectedAnalysis === "optionB" && analysisResults.optionB) analysisToUse = analysisResults.optionB.analysis;
      else if (selectedAnalysis === "both" && analysisResults.optionA && analysisResults.optionB) {
        analysisToUse = `=== ANALYSE RAPIDE (OPTION A) ===\n${analysisResults.optionA.analysis}\n\n=== ANALYSE APPROFONDIE (OPTION B) ===\n${analysisResults.optionB.analysis}`;
      }
    }

    if (!analysisToUse || !identifiedPatient) return;
    try {
      const { data: quotaData } = await fetchJson<{ canGenerate?: boolean }>("/api/me/generations-quota");
      if (quotaData && quotaData.canGenerate === false) {
        setShowNoQuotaModal(true);
        return;
      }
    } catch {
      // ignore, on tente quand même
    }
    const runId = ++phase2RunIdRef.current;
    setAnalysisStage("conclusion");
    setPhase("phase2");
    setProgress("Reformulation en cours...");

    // Si on a choisi une analyse spécifique, on met à jour le state legacy pour qu'il soit synchro
    if (!orthopticAnalysis && selectedAnalysis !== "both") {
      if (selectedAnalysis === "optionA" && analysisResults.optionA) setOrthopticAnalysis(analysisResults.optionA);
      if (selectedAnalysis === "optionB" && analysisResults.optionB) setOrthopticAnalysis(analysisResults.optionB);
    }

    try {
      const conclusionBody = {
        analysis: analysisToUse,
        patientAge: birthdateInfo?.age ?? null,
        patientCodes: identifiedPatient.results.filter((r) => r.anonymousId).map((r) => ({ format: r.format, code: r.anonymousId! })),
        isChild: (birthdateInfo?.age ?? 0) < 18,
        contextNotes,
        rehabilitationCare,
        signature: orthoptistName,
        analysisModel: selectedAnalysis === "both" ? "optionA" : selectedAnalysis, // Utiliser le modèle sélectionné
        ...(conclusionLength === "custom"
          ? { targetWords: Math.max(CONCLUSION_LENGTH_CUSTOM_MIN, Math.min(CONCLUSION_LENGTH_CUSTOM_MAX, conclusionLengthCustom)) }
          : { maxLength: conclusionLength }),
      };
      processLog.startStep("generate-conclusion", "Génération de la conclusion", conclusionBody);
      console.log("Calling generate-conclusion API...");
      let { res: conclRes, data: conclData } = await fetchJson<{ conclusion?: string; _promptSent?: unknown; code?: string }>("/api/generate-conclusion", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(conclusionBody) });
      console.log("Response received:", { status: conclRes.status, ok: conclRes.ok });
      if (conclRes.status === 403 && (conclData as { code?: string }).code === "NO_QUOTA") {
        if (runId !== phase2RunIdRef.current) return;
        setShowNoQuotaModal(true);
        setAnalysisStage("idle");
        return;
      }
      if (conclRes.ok && isConclusionBuggyShared((conclData as { conclusion?: string }).conclusion)) {
        console.warn("Conclusion deemed buggy, retrying...");
        const retry = await fetchJson<{ conclusion?: string; _promptSent?: unknown }>("/api/generate-conclusion", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(conclusionBody) });
        conclData = retry.data;
      }
      processLog.endStep("generate-conclusion", conclData as Record<string, unknown>, undefined, undefined, { inputPrompt: (conclData as { _promptSent?: unknown })._promptSent as string | undefined });
      let rawConclusion = ((conclData as { conclusion?: string }).conclusion ?? "") as string;
      console.log("Conclusion generated, length:", rawConclusion.length);
      if (runId !== phase2RunIdRef.current) return;
      processLog.startStep("deanonymize", "Désanonymisation locale (aucune donnée envoyée)", { description: "Remplacement des codes par le nom du patient, côté client" });
      const formats = identifiedPatient.results.filter((r) => r.value && r.anonymousId);
      let finalConclusion = rawConclusion;
      const firstValue = formats[0]?.value;
      for (const f of formats) {
        const re = new RegExp(`\\[${f.anonymousId}\\]`, "gi");
        finalConclusion = finalConclusion.replace(re, f.value);
      }
      if (firstValue) finalConclusion = finalConclusion.replace(/\[CODE\]/g, firstValue);
      processLog.endStep("deanonymize", { description: "Conclusion désanonymisée côté client", localOnly: true, finalLength: finalConclusion.length, finalConclusion });
      setConclusionResult({ raw: rawConclusion, final: finalConclusion });
      setPhase("done");
      setAnalysisStage("idle");
      processLog.saveLog();
    } catch (err) {
      console.error("Error in runConclusionOnly:", err);
      if (runId !== phase2RunIdRef.current) return;
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e.message.includes("is not valid JSON") ? "Le serveur a renvoyé une erreur (HTML). Vérifiez l'onglet Réseau (F12) ou les logs serveur." : e.message);
      setAnalysisStage("idle");
    }
  }, [orthopticAnalysis, identifiedPatient, birthdateInfo, contextNotes, rehabilitationCare, orthoptistName, conclusionLength, conclusionLengthCustom, processLog, selectedAnalysis, analysisResults]);

  // Génération complète (Gemini + Vertex) quand le formulaire a été modifié
  const runFullPhase2 = useCallback(async () => {
    if (!anonymizationResult || !questionnaireData || !identifiedPatient) return;
    try {
      const { data: quotaData } = await fetchJson<{ canGenerate?: boolean }>("/api/me/generations-quota");
      if (quotaData && quotaData.canGenerate === false) {
        setShowNoQuotaModal(true);
        return;
      }
    } catch {
      // ignore
    }
    const runId = ++phase2RunIdRef.current;
    setValidatingQuestionnaire(true);
    setShowQuestionnaireModal(false);
    setPhase("phase2");
    setAnalysisStage("reasoning");
    setProgress("Analyse orthoptique...");
    try {
      const orthoBody = {
        anonymizedText: anonymizationResult.anonymizedText,
        questionnaire: questionnaireData.categoryAnswers,
        patientAge: birthdateInfo?.age ?? null,
        birthdate: birthdateInfo?.birthdate ?? null,
        contextNotes,
        rehabilitationCare,
        reasoningModel,
        patientFromDb: patientFromDbResult?.found && !patientHistoryDismissed ? { notesExcerpt: patientFromDbResult.notesExcerpt, declensionsCount: patientFromDbResult.declensionsCount, declensions: patientFromDbResult.declensions } : undefined,
      };
      processLog.startStep("analyze-orthoptic", "Analyse orthoptique (IA)", orthoBody);
      const { data: orthoData } = await fetchJson<OrthopticAnalysis & { _promptSent?: unknown }>("/api/analyze-orthoptic", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(orthoBody) });
      processLog.endStep("analyze-orthoptic", orthoData as unknown as Record<string, unknown>, undefined, undefined, { inputPrompt: orthoData._promptSent as string | undefined });
      if (runId !== phase2RunIdRef.current) return;
      setOrthopticAnalysis(orthoData);
      setAnalysisStage("conclusion");
      setProgress("Reformulation en cours...");
      const conclusionBody = {
        analysis: orthoData.analysis,
        patientAge: birthdateInfo?.age ?? null,
        patientCodes: identifiedPatient.results.filter((r) => r.anonymousId).map((r) => ({ format: r.format, code: r.anonymousId! })),
        isChild: (birthdateInfo?.age ?? 0) < 18,
        contextNotes,
        rehabilitationCare,
        signature: orthoptistName,
        ...(conclusionLength === "custom"
          ? { targetWords: Math.max(CONCLUSION_LENGTH_CUSTOM_MIN, Math.min(CONCLUSION_LENGTH_CUSTOM_MAX, conclusionLengthCustom)) }
          : { maxLength: conclusionLength }),
      };
      processLog.startStep("generate-conclusion", "Génération de la conclusion", conclusionBody);
      let { res: conclRes, data: conclData } = await fetchJson<{ conclusion?: string; _promptSent?: unknown; code?: string }>("/api/generate-conclusion", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(conclusionBody) });
      if (conclRes.status === 403 && (conclData as { code?: string }).code === "NO_QUOTA") {
        if (runId !== phase2RunIdRef.current) return;
        setShowNoQuotaModal(true);
        setAnalysisStage("idle");
        setValidatingQuestionnaire(false);
        return;
      }
      if (conclRes.ok && isConclusionBuggyShared((conclData as { conclusion?: string }).conclusion)) {
        const retry = await fetchJson<{ conclusion?: string; _promptSent?: unknown }>("/api/generate-conclusion", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(conclusionBody) });
        conclData = retry.data;
      }
      processLog.endStep("generate-conclusion", conclData as Record<string, unknown>, undefined, undefined, { inputPrompt: (conclData as { _promptSent?: unknown })._promptSent as string | undefined });
      let rawConclusion = ((conclData as { conclusion?: string }).conclusion ?? "") as string;
      if (runId !== phase2RunIdRef.current) return;
      processLog.startStep("deanonymize", "Désanonymisation locale (aucune donnée envoyée)", { description: "Remplacement des codes par le nom du patient, côté client" });
      const formats = identifiedPatient.results.filter((r) => r.value && r.anonymousId);
      let finalConclusion = rawConclusion;
      const firstValue = formats[0]?.value;
      for (const f of formats) {
        const re = new RegExp(`\\[${f.anonymousId}\\]`, "gi");
        finalConclusion = finalConclusion.replace(re, f.value);
      }
      if (firstValue) finalConclusion = finalConclusion.replace(/\[CODE\]/g, firstValue);
      processLog.endStep("deanonymize", { description: "Conclusion désanonymisée côté client", localOnly: true, finalLength: finalConclusion.length, finalConclusion });
      setConclusionResult({ raw: rawConclusion, final: finalConclusion });
      setProgress("");
      setPhase("done");
      setFormModified(false);
      setAnalysisStage("idle");
      processLog.saveLog();
    } catch (err) {
      if (runId !== phase2RunIdRef.current) return;
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e.message.includes("is not valid JSON") ? "Le serveur a renvoyé une erreur (HTML). Vérifiez l'onglet Réseau (F12) ou les logs serveur." : e.message);
      setPhase("questionnaire_modal");
      setShowQuestionnaireModal(true);
      setAnalysisStage("idle");
    } finally {
      setValidatingQuestionnaire(false);
    }
  }, [anonymizationResult, questionnaireData, identifiedPatient, birthdateInfo, contextNotes, rehabilitationCare, reasoningModel, orthoptistName, conclusionLength, conclusionLengthCustom, patientFromDbResult, patientHistoryDismissed, processLog]);

  // Si l'utilisateur arrive à l'étape Génération avec un formulaire modifié, lancer la génération complète automatiquement
  useEffect(() => {
    if (step !== 6 || !formModified || !questionnaireData || fullPhase2OnStep6Ref.current) return;
    fullPhase2OnStep6Ref.current = true;
    runFullPhase2();
  }, [step, formModified, questionnaireData, runFullPhase2]);

  // Plus de démarrage automatique dès l'étape 3. 
  // L'analyse duale est maintenant déclenchée manuellement par le bouton Suivant de l'étape 5.
  /*
  useEffect(() => {
    if (step !== 3 || !questionnaireData || !anonymizationResult || !identifiedPatient || geminiStartedRef.current) return;
    geminiStartedRef.current = true;
    runOrthopticAnalysisOnly();
  }, [step, questionnaireData, anonymizationResult, identifiedPatient, runOrthopticAnalysisOnly]);
  */

  // Si l'utilisateur a modifié les données Patient puis passe à l'étape suivante, relancer l'analyse
  useEffect(() => {
    if (step !== 4 || !patientStepModifiedRef.current) return;
    // Modif: plus de relance automatique. On attend que l'utilisateur re-valide le formulaire (étape 5 -> 6).
    /*
    patientStepModifiedRef.current = false;
    setOrthopticAnalysis(null);
    setConclusionResult(null);
    conclusionStartedRef.current = false;
    setAnalysisStage("reasoning");
    runOrthopticAnalysisOnly();
    */
    // On marque juste que c'est modif pour que le btn suivant relance
    // (patientStepModifiedRef reste true jusqu'à l'action manuelle)
  }, [step, runOrthopticAnalysisOnly]);

  // Charger la liste des patients à l'étape Patient
  useEffect(() => {
    if (step !== 3) return;
    let cancelled = false;
    setPatientsListLoading(true);
    fetchJson<{ patients?: { id: string; firstName: string; lastName: string }[] }>("/api/patients")
      .then(({ data }) => {
        if (!cancelled && Array.isArray(data.patients)) setPatientsList(data.patients);
      })
      .catch(() => { if (!cancelled) setPatientsList([]); })
      .finally(() => { if (!cancelled) setPatientsListLoading(false); });
    return () => { cancelled = true; };
  }, [step]);

  const loadPatientFromDb = useCallback(async (patientId: string) => {
    try {
      const { res, data: detail } = await fetchJson<{ id?: string; firstName?: string; lastName?: string; nameDeclensions?: string[] | string; notes?: string; notesAnonymizationMap?: string | null; birthDate?: string | null }>(`/api/patients/${patientId}`);
      if (!res.ok || !detail?.id) return;
      const decl = Array.isArray(detail.nameDeclensions) ? detail.nameDeclensions : typeof detail.nameDeclensions === "string" ? (() => { try { return JSON.parse(detail.nameDeclensions || "[]"); } catch { return []; } })() : [];
      setPatientFromDbResult({
        found: true,
        notesExcerpt: detail.notes ? String(detail.notes).substring(0, 800) : undefined,
        notesAnonymizationMap: detail.notesAnonymizationMap ?? undefined,
        patientDisplayName: detail.lastName && detail.firstName ? `${detail.lastName} ${detail.firstName}` : undefined,
        declensions: decl,
        declensionsCount: decl?.length ?? 0,
      });
      setPatientHistoryDismissed(false);
      if (detail.birthDate && /^\d{4}-\d{2}-\d{2}$/.test(String(detail.birthDate))) {
        setPatientBirthDate(String(detail.birthDate));
      }
    } catch {
      setPatientFromDbResult({ found: false });
    }
  }, []);

  // Démarrer la conclusion Vertex (réformulation seule) quand l'analyse est prête, sur Formulaire ou Génération, sans modification
  useEffect(() => {
    if ((step !== 5 && step !== 6) || !orthopticAnalysis || formModified || conclusionStartedRef.current) return;
    conclusionStartedRef.current = true;
    runConclusionOnly();
  }, [step, orthopticAnalysis, formModified, runConclusionOnly]);

  // À l'étape Génération : DÉSACTIVÉ en faveur du déclenchement manuel "Suivant" depuis l'étape 5
  /*
  useEffect(() => {
    if (
      step !== 6 ||
      !questionnaireData ||
      !anonymizationResult ||
      !identifiedPatient ||
      orthopticAnalysis != null ||
      formModified ||
      analysisStage !== "idle" ||
      processLog.currentStep != null
    )
      return;
    runOrthopticAnalysisOnly();
  }, [step, questionnaireData, anonymizationResult, identifiedPatient, orthopticAnalysis, formModified, analysisStage, runOrthopticAnalysisOnly]);
  */

  const handleLaunch = () => {
    if (!file) {
      setError("Sélectionnez un fichier.");
      return;
    }
    if (backgroundPhase === "ready" && questionnaireData) {
      setPhase("questionnaire_modal");
      setShowQuestionnaireModal(true);
      return;
    }
    runPhase1(file, false);
  };

  const getBilanDownloadFilename = (extension: "pdf" | "docx"): string => {
    const nomFromId = identifiedPatient?.results?.find((r) => r.format === "NOM" || r.format?.toUpperCase() === "NOM")?.value?.toUpperCase().replace(/\s+/g, "_");
    const prenomFromId = identifiedPatient?.results?.find((r) => r.format === "Prénom" || r.format?.toUpperCase() === "PRENOM")?.value?.trim();
    const parts = patientName?.trim().split(/\s+/).filter(Boolean) ?? [];
    const nom = nomFromId ?? (parts.length > 0 ? parts[0].toUpperCase().replace(/\s+/g, "_") : "");
    const prenom = prenomFromId ?? (parts.length > 1 ? parts.slice(1).join(" ") : parts[0] ?? "");
    const prenomF = prenom ? prenom.charAt(0).toUpperCase() + prenom.slice(1).toLowerCase().replace(/\s+/g, "_") : "";
    const dateStr = (patientBirthDate || (birthdateInfo?.birthdate?.replace(/\//g, "_") ?? new Date().toISOString().split("T")[0])).replace(/\//g, "_");
    if (nom && prenomF && dateStr) return `${nom}_${prenomF}_${dateStr}.${extension}`;
    return `bilan_complet_${dateStr}.${extension}`;
  };

  const stepsConfig = [
    { id: 1, label: "Téléverser", icon: FileUp },
    { id: 2, label: "Contexte", icon: MessageSquare },
    { id: 3, label: "Patient", icon: User },
    { id: 4, label: "Longueur conclusion", icon: Ruler },
    { id: 5, label: "Formulaire interactif", icon: ListChecks },
    { id: 6, label: "Génération", icon: FileOutput },
  ];
  const isNotPhase1 = phase !== "phase1";

  return (
    <div className="space-y-6">
      {user?.role === "admin" && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => processLog.downloadLog()}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-xs font-semibold hover:bg-slate-200 border border-slate-200 transition-colors"
            title="Télécharger les logs techniques JSON"
          >
            <FileJson className="h-3.5 w-3.5" />
            Logs JSON (debug)
          </button>
        </div>
      )}
      {isNotPhase1 && (
        <div className="rounded-2xl border border-slate-200 bg-white/80 backdrop-blur p-4 shadow-sm">
          <div className="flex items-center justify-between gap-2">
            {stepsConfig.map((s, i) => {
              const Icon = s.icon;
              const isActive = step === s.id;
              const isPast = step > s.id;
              const isFuture = s.id > step;
              return (
                <div key={s.id} className="flex items-center flex-1 min-w-0">
                  <button
                    type="button"
                    onClick={() => setStep(s.id)}
                    aria-current={isActive ? "step" : undefined}
                    className={cn(
                      "flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium transition-all w-full min-w-0",
                      isActive ? "bg-primary-500 text-white shadow-md" : isPast ? "bg-primary-50 text-primary-700 hover:bg-primary-100" : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                    )}
                  >
                    <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", isActive ? "bg-white/20" : isPast ? "bg-primary-200/50" : "bg-slate-200")}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="truncate hidden sm:inline">{s.label}</span>
                  </button>
                  {i < stepsConfig.length - 1 && <ChevronRight className="h-5 w-5 text-slate-300 shrink-0 mx-0.5" aria-hidden />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-6">
        {error && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">{error}</div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <FileUp className="h-5 w-5 text-primary-600" />
              Source des données
            </h2>
            <p className="text-sm text-slate-600">Choisissez comment fournir les données d'analyse : téléverser un fichier ou coller directement le texte.</p>

            {/* Mode selection */}
            <div className="flex gap-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="inputMode"
                  value="file"
                  checked={inputMode === "file"}
                  onChange={(e) => {
                    setInputMode(e.target.value as "file" | "text");
                    setError(null);
                    setBackgroundPhase("idle");
                  }}
                  className="w-4 h-4 text-primary-600 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-slate-700">Téléverser un fichier</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="inputMode"
                  value="text"
                  checked={inputMode === "text"}
                  onChange={(e) => {
                    setInputMode(e.target.value as "file" | "text");
                    setError(null);
                    setBackgroundPhase("idle");
                  }}
                  className="w-4 h-4 text-primary-600 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-slate-700">Coller le texte</span>
              </label>
            </div>

            {/* File upload mode */}
            {inputMode === "file" && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Fichier à traiter</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null;
                    setFile(f);
                    setError(null);
                    backgroundRunIdRef.current += 1;
                    geminiStartedRef.current = false;
                    conclusionStartedRef.current = false;
                    patientStepModifiedRef.current = false;
                    setFormModified(false);
                    if (f) {
                      setBackgroundPhase("idle");
                      setStep(1);
                      setExtractionResult(null);
                      setIdentifiedPatient(null);
                      setPatientFromDbResult(null);
                      setAnonymizationResult(null);
                      setBirthdateInfo(null);
                      setQuestionnaireData(null);
                      setPatientName("");
                      setPatientBirthDate("");
                      setOrthopticAnalysis(null);
                      setConclusionResult(null);
                      setPhase("idle");
                      fullPhase2OnStep6Ref.current = false;
                      setAnalysisStage("idle");
                      setAnalysisElapsedSeconds(0);
                    } else {
                      setBackgroundPhase("idle");
                      setStep(1);
                    }
                  }}
                  className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary-50 file:text-primary-700"
                />
                {file && <p className="text-xs text-slate-500 mt-1">{file.name} ({(file.size / 1024).toFixed(1)} Ko)</p>}
                {backgroundPhase === "running" && (
                  <p className="text-xs text-primary-600 mt-1 flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {backgroundProgress || "Préparation en cours..."}
                  </p>
                )}
                {backgroundPhase === "ready" && file && <p className="text-xs text-emerald-600 mt-1">Prêt – passez à l'étape suivante</p>}
              </div>
            )}

            {/* Text paste mode */}
            {inputMode === "text" && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Texte d'analyse</label>
                <textarea
                  value={pastedText}
                  onChange={(e) => {
                    setPastedText(e.target.value);
                    setError(null);
                    backgroundRunIdRef.current += 1;
                    geminiStartedRef.current = false;
                    conclusionStartedRef.current = false;
                    patientStepModifiedRef.current = false;
                    setFormModified(false);
                    if (e.target.value.trim()) {
                      setBackgroundPhase("idle");
                      setStep(1);
                      setExtractionResult(null);
                      setIdentifiedPatient(null);
                      setPatientFromDbResult(null);
                      setAnonymizationResult(null);
                      setBirthdateInfo(null);
                      setQuestionnaireData(null);
                      setPatientName("");
                      setPatientBirthDate("");
                      setOrthopticAnalysis(null);
                      setConclusionResult(null);
                      setPhase("idle");
                      fullPhase2OnStep6Ref.current = false;
                      setAnalysisStage("idle");
                      setAnalysisElapsedSeconds(0);
                    } else {
                      setBackgroundPhase("idle");
                      setStep(1);
                    }
                  }}
                  placeholder="Collez ici le texte complet de l'analyse orthoptique..."
                  rows={12}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                />
                {pastedText.trim() && (
                  <p className="text-xs text-slate-500 mt-1">
                    {pastedText.trim().length} caractères
                  </p>
                )}
                {backgroundPhase === "running" && (
                  <p className="text-xs text-primary-600 mt-1 flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {backgroundProgress || "Traitement en cours..."}
                  </p>
                )}
                {backgroundPhase === "ready" && pastedText.trim() && (
                  <p className="text-xs text-emerald-600 mt-1">✓ Traitement terminé – cliquez sur "Suivant" pour continuer</p>
                )}
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary-600" />
              Contexte / mots-clés
            </h2>
            <p className="text-sm text-slate-600">Contexte optionnel pour la conclusion (RDV de suivi, évolution…). La prise en charge de la rééducation fait partie du contexte.</p>

            {/* Choix du médecin */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Médecin correspondant (pour l'entête du bilan)</label>
              <div className="flex gap-2">
                <select
                  value={selectedDoctorId}
                  onChange={(e) => setSelectedDoctorId(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Aucun (ou gérer plus tard)</option>
                  {doctorsList.map((doc) => (
                    <option key={doc.id} value={doc.id}>
                      {doc.title ? `${doc.title} ` : ""}{doc.firstName} {doc.lastName}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowAddDoctorModal(true)}
                  className="px-3 py-2 rounded-lg border border-primary-300 bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors flex items-center gap-1"
                  title="Ajouter un médecin"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              {doctorsList.length === 0 && (
                <p className="text-xs text-slate-500 mt-1">Aucun médecin enregistré. Cliquez sur le bouton + pour en ajouter.</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Contexte (optionnel)</label>
              <textarea
                value={contextNotes}
                onChange={(e) => setContextNotes(e.target.value)}
                placeholder="Ex: RDV de suivi dans 3 mois, amélioration depuis le dernier bilan..."
                rows={4}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex items-center gap-3 pt-2">
              <span className="text-sm font-medium text-slate-700">Prise en charge de la rééducation</span>
              <button
                type="button"
                role="switch"
                aria-checked={rehabilitationCare}
                onClick={() => setRehabilitationCare((v) => !v)}
                className={cn("relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors", rehabilitationCare ? "bg-primary-500" : "bg-slate-200")}
              >
                <span className={cn("pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition", rehabilitationCare ? "translate-x-5" : "translate-x-1")} />
              </button>
              <span className="text-sm text-slate-600">{rehabilitationCare ? "Oui" : "Non"}</span>
            </div>
            {backgroundPhase === "running" && (
              <div className="rounded-lg bg-primary-50 border border-primary-200 p-2.5 flex items-center gap-2 mt-2">
                <Loader2 className="h-4 w-4 text-primary-600 animate-spin flex-shrink-0" />
                <span className="text-xs text-primary-700">{backgroundProgress || "Analyse du document en cours en arrière-plan…"}</span>
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <User className="h-5 w-5 text-primary-600" />
              Patient et paramètres
            </h2>
            <p className="text-sm text-slate-600">Vérifiez le patient identifié, l&apos;historique des séances et les données clés. L&apos;analyse Gemini démarre en arrière-plan dès l&apos;affichage de cette étape. En cas de modification, elle sera relancée à l&apos;étape suivante.</p>
            {patientFromDbResult?.found === false && backgroundPhase === "ready" && (
              <p className="text-sm text-slate-500">Aucun patient trouvé en base pour ce nom. L&apos;analyse utilisera uniquement les données du document.</p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <label className="block text-sm font-medium text-slate-700 mb-1">Patient</label>
                <input
                  type="text"
                  value={patientName}
                  onChange={(e) => { setPatientName(e.target.value); patientStepModifiedRef.current = true; setShowPatientDropdown(true); }}
                  onFocus={() => setShowPatientDropdown(true)}
                  onBlur={() => {
                    patientDropdownBlurRef.current = setTimeout(() => setShowPatientDropdown(false), 200);
                  }}
                  placeholder="Choisir un patient ou saisie manuelle"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:ring-2 focus:ring-primary-500"
                  autoComplete="off"
                />
                {backgroundPhase === "running" && (
                  <p className="text-xs text-primary-600 mt-1 flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Identification en cours...
                  </p>
                )}
                {showPatientDropdown && (patientsList.length > 0 || patientsListLoading) && (
                  <div
                    className="absolute z-10 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-48 overflow-y-auto"
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {patientsListLoading ? (
                      <div className="p-3 text-sm text-slate-500 flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Chargement des patients...
                      </div>
                    ) : (
                      (() => {
                        const q = patientName.trim().toLowerCase();
                        const filtered = q
                          ? patientsList.filter((p) => {
                            const full = `${(p.lastName ?? "").toLowerCase()} ${(p.firstName ?? "").toLowerCase()}`;
                            const full2 = `${(p.firstName ?? "").toLowerCase()} ${(p.lastName ?? "").toLowerCase()}`;
                            return full.includes(q) || full2.includes(q) || full.split(/\s+/).some((w) => w.startsWith(q)) || full2.split(/\s+/).some((w) => w.startsWith(q));
                          })
                          : patientsList;
                        if (filtered.length === 0) return <div className="p-3 text-sm text-slate-500">Aucun patient trouvé</div>;
                        return filtered.map((p) => {
                          const display = `${p.lastName} ${p.firstName}`;
                          return (
                            <button
                              key={p.id}
                              type="button"
                              className="w-full text-left px-3 py-2.5 text-sm text-slate-800 hover:bg-primary-50 border-b border-slate-100 last:border-b-0 first:rounded-t-lg last:rounded-b-lg"
                              onMouseDown={() => {
                                if (patientDropdownBlurRef.current) clearTimeout(patientDropdownBlurRef.current);
                                setPatientName(display);
                                setShowPatientDropdown(false);
                                patientStepModifiedRef.current = true;
                                loadPatientFromDb(p.id);
                              }}
                            >
                              {display}
                            </button>
                          );
                        });
                      })()
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Date de naissance</label>
                <input
                  type="date"
                  value={patientBirthDate}
                  onChange={(e) => { setPatientBirthDate(e.target.value); patientStepModifiedRef.current = true; }}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Signature</label>
                <input
                  type="text"
                  value={orthoptistName}
                  onChange={(e) => { setOrthoptistName(e.target.value); patientStepModifiedRef.current = true; }}
                  placeholder="Ex: Lucca Rouxel"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            {patientFromDbResult?.found && patientFromDbResult.notesExcerpt && patientHistoryDismissed && (
              <p className="text-sm text-slate-500">
                L&apos;historique du patient a été retiré pour cette génération.{" "}
                <button type="button" onClick={() => setPatientHistoryDismissed(false)} className="text-primary-600 hover:underline font-medium">
                  Réafficher l&apos;historique
                </button>
              </p>
            )}
            {patientFromDbResult?.found && patientFromDbResult.notesExcerpt && !patientHistoryDismissed && (
              <div className="rounded-xl border border-slate-200 bg-slate-50/50 overflow-hidden">
                <div className="px-4 py-3 bg-white border-b border-slate-200 flex items-center justify-between gap-2">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-800">Données patient</h4>
                    <p className="text-xs text-slate-500 mt-0.5">Historique et données clés du patient</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setPatientHistoryDismissed(true); patientStepModifiedRef.current = true; }}
                    className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-600 hover:text-red-600 hover:bg-red-50 border border-slate-200 hover:border-red-200 transition-colors"
                    title="Ne pas utiliser cet historique pour cette génération"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Supprimer l&apos;historique
                  </button>
                </div>
                <div className="p-4 max-h-48 overflow-y-auto">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">
                    {deanonymizeText(
                      patientFromDbResult.notesExcerpt,
                      parseDeanonymMap(patientFromDbResult.notesAnonymizationMap) ??
                      (patientFromDbResult.patientDisplayName
                        ? buildFallbackDeanonymMapForNotes(patientFromDbResult.notesExcerpt, patientFromDbResult.patientDisplayName)
                        : null)
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Ruler className="h-5 w-5 text-primary-600" />
              Longueur de la conclusion
            </h2>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Longueur</label>
              <div className="flex gap-2 flex-wrap">
                {CONCLUSION_LENGTH_OPTIONS.map((length) => (
                  <button
                    key={length}
                    type="button"
                    onClick={() => setConclusionLength(length)}
                    className={cn(
                      "flex-1 min-w-[100px] py-3 px-4 rounded-xl text-sm font-medium transition-all border-2",
                      conclusionLength === length ? "bg-primary-600 text-white border-primary-600 shadow-md" : "bg-slate-50 text-slate-600 border-slate-200 hover:border-primary-300 hover:bg-primary-50/50"
                    )}
                  >
                    {CONCLUSION_LENGTH_LABELS[length]}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setConclusionLength("custom")}
                  className={cn(
                    "flex-1 min-w-[100px] py-3 px-4 rounded-xl text-sm font-medium transition-all border-2",
                    conclusionLength === "custom" ? "bg-primary-600 text-white border-primary-600 shadow-md" : "bg-slate-50 text-slate-600 border-slate-200 hover:border-primary-300 hover:bg-primary-50/50"
                  )}
                >
                  {CONCLUSION_LENGTH_CUSTOM_LABEL}
                </button>
              </div>
              {conclusionLength === "custom" && (
                <div className="mt-3 flex items-center gap-2">
                  <label htmlFor="conclusion-custom-chars" className="text-sm text-slate-600">Longueur cible (mots) :</label>
                  <input
                    id="conclusion-custom-chars"
                    type="number"
                    min={CONCLUSION_LENGTH_CUSTOM_MIN}
                    max={CONCLUSION_LENGTH_CUSTOM_MAX}
                    value={conclusionLengthCustom}
                    onChange={(e) => setConclusionLengthCustom(Math.max(CONCLUSION_LENGTH_CUSTOM_MIN, Math.min(CONCLUSION_LENGTH_CUSTOM_MAX, parseInt(e.target.value, 10) || CONCLUSION_LENGTH_CUSTOM_MIN)))}
                    className="w-24 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                  <span className="text-xs text-slate-500">entre {CONCLUSION_LENGTH_CUSTOM_MIN} et {CONCLUSION_LENGTH_CUSTOM_MAX} mots</span>
                </div>
              )}
            </div>
            {backgroundPhase === "running" && (
              <div className="rounded-lg bg-primary-50 border border-primary-200 p-2.5 flex items-center gap-2">
                <Loader2 className="h-4 w-4 text-primary-600 animate-spin flex-shrink-0" />
                <span className="text-xs text-primary-700">{backgroundProgress || "Analyse du document en cours en arrière-plan…"}</span>
              </div>
            )}
          </div>
        )}

        {step === 5 && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <ListChecks className="h-5 w-5 text-primary-600" />
              Formulaire interactif
            </h2>
            {questionnaireData ? (
              <>
                <p className="text-sm text-slate-600">Vérifiez les extraits du document et corrigez si besoin l'interprétation avant de lancer l'analyse.</p>
                <div className="space-y-6 pt-2">
                  {(() => {
                    const sections = questionnaireData.selectedCategories
                      .map((category) => {
                        const catData = questionnaireData.categoryAnswers[category];
                        if (!catData) return null;
                        const answerEntries = Object.entries(catData.answers);
                        if (answerEntries.length === 0) return null;
                        const categoryLabel = category.replace(/_/g, " ");
                        return (
                          <section key={category} className="rounded-xl border border-slate-200 bg-slate-50/50 overflow-hidden">
                            <div className="px-4 py-3 bg-white border-b border-slate-200">
                              <h4 className="text-sm font-semibold text-slate-800 tracking-tight">{categoryLabel}</h4>
                            </div>
                            <div className="p-4 space-y-6">
                              {answerEntries.map(([question, answer], idx) => {
                                let options = catData.availableOptions[question] || [];
                                if (options.length === 0 && answer.selected) options = [answer.selected];
                                const hasSourceText = answer.sourceText != null && String(answer.sourceText).trim() !== "";
                                // Titre = libellé cohérent avec option.json (clé), pas la citation
                                const keyLooksLikeCitation =
                                  question.trim().startsWith("- ") ||
                                  question.length > 70 ||
                                  (hasSourceText && answer.sourceText!.trim() === question.trim()) ||
                                  /\d\/10|ppc|rdc|:\s*\d|cm\b|d\'?\d|d\d\s|c\d|^avl\s|avp\s|exophorie|strabisme\s*:/i.test(question);
                                const questionTitle =
                                  !keyLooksLikeCitation && !/^\d+$/.test(question) ? question : `Question ${idx + 1}`;
                                return (
                                  <div key={question} className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
                                    <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
                                      <h5 className="text-sm font-semibold text-slate-800">{questionTitle}</h5>
                                    </div>
                                    <div className="flex flex-col sm:flex-row min-h-0">
                                      <div className="sm:w-1/2 p-4 border-b sm:border-b-0 sm:border-r border-slate-200 bg-primary-50/20">
                                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Citation</p>
                                        {hasSourceText ? (
                                          <blockquote className="text-sm text-slate-700 italic pl-3 border-l-2 border-primary-300">« {answer.sourceText!.trim()} »</blockquote>
                                        ) : (
                                          <p className="text-sm text-slate-500 italic">Aucune citation</p>
                                        )}
                                      </div>
                                      <div className="sm:w-1/2 p-4">
                                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Interprétation</p>
                                        <div className="flex flex-wrap gap-2">
                                          {options.map((opt) => (
                                            <label key={opt} className={cn("inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium cursor-pointer transition-colors", answer.selected === opt ? "border-primary-500 bg-primary-50 text-primary-800" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300")}>
                                              <input type="radio" name={`${category}-${idx}`} value={opt} checked={answer.selected === opt} onChange={() => updateQuestionnaireAnswer(category, question, opt)} className="sr-only" />
                                              {opt}
                                            </label>
                                          ))}
                                        </div>
                                        {options.length === 0 && (
                                          <select value={answer.selected} onChange={(e) => updateQuestionnaireAnswer(category, question, e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-900 text-sm focus:ring-2 focus:ring-primary-500">
                                            <option value={answer.selected}>{answer.selected}</option>
                                          </select>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </section>
                        );
                      })
                      .filter(Boolean);
                    if (sections.length === 0) {
                      return (
                        <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-6 text-center">
                          <p className="text-slate-600">Aucune donnée clinique n'a pu être extraite pour ce document.</p>
                          <p className="text-sm text-slate-500 mt-1">Vous pouvez lancer l'analyse tout de même ci-dessous.</p>
                          {anonymizationResult && (
                            <button
                              type="button"
                              onClick={regenerateQuestionnaire}
                              disabled={isRegeneratingQuestionnaire}
                              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500 text-white font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {isRegeneratingQuestionnaire ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Regénération…
                                </>
                              ) : (
                                "Regénérer l'extraction"
                              )}
                            </button>
                          )}
                        </div>
                      );
                    }
                    return sections;
                  })()}
                </div>
              </>
            ) : file && (backgroundPhase === "running" || backgroundPhase === "idle") ? (
              <div className="flex flex-col items-center justify-center py-8 gap-3 rounded-xl border border-slate-200 bg-slate-50/50">
                <LottieLoading size={200} />
                <p className="text-sm font-medium text-slate-700">Préparation du formulaire en cours...</p>
                <p className="text-xs text-slate-500">{backgroundProgress || "Extraction et identification en arrière-plan."}</p>
              </div>
            ) : backgroundPhase === "error" ? (
              <p className="text-sm text-amber-700">Une erreur s'est produite lors de la préparation. Vous pouvez réessayer en changeant de fichier à l'étape 1.</p>
            ) : !file ? (
              <p className="text-sm text-slate-600">Sélectionnez un fichier à l'étape 1 pour générer le formulaire.</p>
            ) : null}
          </div>
        )}

        {step === 6 && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <FileOutput className="h-5 w-5 text-primary-600" />
              Génération
            </h2>
            {/* UI DUAL ANALYSIS A/B TEST : Sélection et Comparaison des CONCLUSIONS finales */}
            {(analysisStage === "reasoning" || (phase === "phase2" && !conclusionResult?.final && analysisStage !== "conclusion")) && (
              <div className="space-y-6">

                {/* CAS 1 : En cours de génération (Flash ou Pro pas encore prêt) */}
                {/* On attend que LES DEUX soient finis avant d'afficher quoi que ce soit */}
                {(!conclusionResultsDuel.optionA || !conclusionResultsDuel.optionB) && !analysisErrors.optionA && !analysisErrors.optionB && (
                  <div className="rounded-xl border border-slate-200 bg-white p-8 text-center space-y-6 shadow-sm">
                    <div className="flex justify-center">
                      <LottieLoading size={250} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">Analyse et rédaction en cours...</h3>
                      <p className="text-slate-500 mt-1">L'IA génère deux propositions de conclusion différentes.</p>
                    </div>

                    {/* Barre de progression 45s */}
                    <div className="max-w-md mx-auto w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                      <div
                        className="h-full bg-primary-500 rounded-full transition-all duration-300 ease-linear"
                        style={{ width: `${Math.min(100, (analysisElapsedSeconds / 45) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* CAS 2 : Erreur sur l'un des deux */}
                {(analysisErrors.optionA || analysisErrors.optionB) && (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center text-red-800">
                    <p className="font-bold mb-2">Une erreur est survenue lors de la génération.</p>
                    <p className="text-sm">{analysisErrors.optionA || analysisErrors.optionB}</p>
                    <button onClick={() => setAnalysisStage("idle")} className="mt-4 px-4 py-2 bg-white border border-red-200 rounded-lg text-sm font-medium hover:bg-red-50">
                      Réessayer
                    </button>
                  </div>
                )}

                {/* CAS 3 : Les deux sont prêts -> Affichage comparatif "Blind Test" */}
                {conclusionResultsDuel.optionA && conclusionResultsDuel.optionB && (
                  <>
                    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl mb-4">
                      <p className="text-sm text-emerald-800 font-medium text-center flex items-center justify-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        Les deux propositions sont prêtes. Choisissez celle qui vous convient le mieux.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* PROPOSITION A (OPTION A) */}
                      <div className={cn("rounded-xl border p-4 transition-all flex flex-col h-full", selectedAnalysis === "optionA" ? "border-primary-500 ring-1 ring-primary-500 shadow-md" : "border-slate-200 bg-white")}>
                        <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-100">
                          <h3 className="font-bold text-slate-800 flex items-center gap-2">
                            <FileText className="h-4 w-4 text-slate-500" />
                            Option A
                          </h3>
                          <span className="text-xs font-medium text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full flex items-center gap-1">
                            Prêt
                          </span>
                        </div>

                        <div className="flex-1 bg-slate-50 rounded-lg p-3 text-sm text-slate-700 whitespace-pre-wrap font-serif h-96 overflow-y-auto mb-4 border border-slate-200">
                          {conclusionResultsDuel.optionA}
                        </div>

                        <button
                          onClick={() => {
                            if (conclusionResultsDuel.optionA) {
                              setSelectedAnalysis("optionA");
                              conclusionStartedRef.current = true;
                              setConclusionResult({ raw: conclusionResultsDuel.optionA, final: conclusionResultsDuel.optionA });
                              setEditedConclusion(conclusionResultsDuel.optionA);
                              setPhase("done");
                              setAnalysisStage("idle");
                              // Update compatibility state
                              if (analysisResults.optionA) setOrthopticAnalysis(analysisResults.optionA);
                              // Log du choix
                              processLog.startStep("selection-proposition", "Choix de la Option A", { model: "optionA" });
                              processLog.endStep("selection-proposition", { selected: "optionA" });
                              processLog.saveLog();
                            }

                          }}
                          className={cn(
                            "w-full py-3 px-4 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2",
                            "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400"
                          )}
                        >
                          Choisir la Option A
                        </button>
                      </div>

                      {/* OPTION B: APPROFONDIE (PRO) */}
                      <div className={cn("rounded-xl border p-4 transition-all flex flex-col h-full", selectedAnalysis === "optionB" ? "border-primary-500 ring-1 ring-primary-500 shadow-md" : "border-slate-200 bg-white")}>
                        <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-100">
                          <h3 className="font-bold text-slate-800 flex items-center gap-2">
                            <FileText className="h-4 w-4 text-slate-500" />
                            Option B
                          </h3>
                          <span className="text-xs font-medium text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full flex items-center gap-1">
                            Prêt
                          </span>
                        </div>

                        <div className="flex-1 bg-slate-50 rounded-lg p-3 text-sm text-slate-700 whitespace-pre-wrap font-serif h-96 overflow-y-auto mb-4 border border-slate-200">
                          {conclusionResultsDuel.optionB}
                        </div>

                        <button
                          onClick={() => {
                            if (conclusionResultsDuel.optionB) {
                              setSelectedAnalysis("optionB");
                              conclusionStartedRef.current = true;
                              setConclusionResult({ raw: conclusionResultsDuel.optionB, final: conclusionResultsDuel.optionB });
                              setEditedConclusion(conclusionResultsDuel.optionB);
                              setPhase("done");
                              setAnalysisStage("idle");
                              // Update compatibility state
                              if (analysisResults.optionB) setOrthopticAnalysis(analysisResults.optionB);
                              // Log du choix
                              processLog.startStep("selection-proposition", "Choix de la Option B", { model: "optionB" });
                              processLog.endStep("selection-proposition", { selected: "optionB" });
                              processLog.saveLog();
                            }

                          }}
                          className={cn(
                            "w-full py-3 px-4 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2",
                            "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400"
                          )}
                        >
                          Choisir la Option B
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* PHASE CONCLUSION (Reformulation seule) */}
            {analysisStage === "conclusion" && (
              <div className="rounded-2xl border border-primary-200 bg-primary-50/30 p-6 flex items-center gap-4">
                <Loader2 className="h-8 w-8 text-primary-600 animate-spin flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900">Reformulation de la conclusion...</p>
                  <p className="text-sm text-slate-600 mt-1">Génération du rapport final à partir de l'analyse sélectionnée.</p>
                  <div className="mt-3">
                    <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                      <div className="h-full bg-primary-500 transition-all duration-1000" style={{ width: "90%" }} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {phase === "done" && conclusionResult?.final && (
              <div className="space-y-6">
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <label className="block text-xs font-medium text-slate-500 mb-2">Conclusion (modifiable avant téléchargement)</label>
                  <textarea
                    value={editedConclusion}
                    onChange={(e) => setEditedConclusion(e.target.value)}
                    className="w-full min-h-[180px] rounded-lg border border-slate-300 bg-white p-3 text-sm text-slate-700 whitespace-pre-wrap focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    placeholder="Conclusion…"
                  />
                </div>
                <div className="flex flex-wrap gap-3">
                  {(() => {
                    const isDocOrDocx = file?.name && (file.name.toLowerCase().endsWith(".docx") || (file.name.toLowerCase().endsWith(".doc") && !file.name.toLowerCase().endsWith(".docx")));
                    const conclusionText = editedConclusion.trim() || (conclusionResult?.final ?? "");

                    const handleDownloadWord = async () => {
                      if (!file || !conclusionText) {
                        toast.error("Données insuffisantes pour générer le Word");
                        return;
                      }
                      setIsDownloadingWord(true);
                      try {
                        const formData = new FormData();
                        formData.append("file", file);
                        formData.append("conclusion", conclusionText);

                        const res = await fetch("/api/bilan/append-conclusion-docx", {
                          method: "POST",
                          body: formData,
                        });

                        if (!res.ok) {
                          const errData = await res.json().catch(() => ({}));
                          throw new Error(errData.error || "Erreur lors de la génération du Word");
                        }

                        const blob = await res.blob();
                        const filename = getBilanDownloadFilename("docx");
                        saveAs(blob, filename);
                        toast.success("Bilan Word téléchargé");
                      } catch (err) {
                        console.error("Error downloading Word:", err);
                        toast.error(err instanceof Error ? err.message : "Erreur lors du téléchargement");
                      } finally {
                        setIsDownloadingWord(false);
                      }
                    };

                    const handleVisualize = async () => {
                      if (!anonymizationResult || !questionnaireData || (!editedConclusion && !conclusionResult?.final)) {
                        toast.error("Données insuffisantes pour générer la visualisation");
                        return;
                      }

                      setIsVisualizing(true);
                      try {
                        const { data: mappedData } = await fetchJson<any>("/api/reports/map-template", {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            questionnaireData,
                            conclusion: editedConclusion || conclusionResult?.final,
                            anonymizedText: anonymizationResult.anonymizedText, // AJOUT: Texte complet pour l'Anamnèse
                          }),
                        });

                        // Deanonymize metadata
                        const deanonymMap = anonymizationResult.replacements ? buildDeanonymizationMap(anonymizationResult.replacements) : {};

                        // Récupérer le médecin sélectionné
                        const selectedDoctor = doctorsList.find(d => d.id === selectedDoctorId);
                        const prescripteurLabel = selectedDoctor
                          ? `${selectedDoctor.title ? selectedDoctor.title + " " : ""}${selectedDoctor.lastName} ${selectedDoctor.firstName}${selectedDoctor.city ? " (" + selectedDoctor.city + ")" : ""}`
                          : "Bilan Orthoptique";

                        const reportData = {
                          meta: {
                            dossierRef: "#" + (Math.random() * 10000).toFixed(0),
                            date: new Date().toLocaleDateString("fr-FR"),
                            praticien: {
                              nom: orthoptistName || "Orthoptiste",
                              email: user?.email || "",
                              adresse: userAddress || "",
                              cabinet: userCabinet || "",
                              signature: userSignature || "",
                              rpps: userRpps || "",
                            },
                            prescripteur: prescripteurLabel,
                          },
                          patient: {
                            nom: patientName,
                            details: `Né(e) le ${patientBirthDate || "--"}`,
                          },
                          contenu: mappedData.contenu,
                        };

                        // Deanonymize the content recursively
                        const deanonymizeRecursive = (obj: any): any => {
                          if (typeof obj === "string") {
                            return deanonymizeText(obj, deanonymMap);
                          } else if (Array.isArray(obj)) {
                            return obj.map(deanonymizeRecursive);
                          } else if (obj !== null && typeof obj === "object") {
                            const newObj: any = {};
                            for (const key in obj) {
                              newObj[key] = deanonymizeRecursive(obj[key]);
                            }
                            return newObj;
                          }
                          return obj;
                        };

                        reportData.contenu = deanonymizeRecursive(reportData.contenu);

                        const { generateReportHtml } = await import("@/lib/report-preview-helper");
                        // Cache busting signature
                        const html = await generateReportHtml(reportData as any, `/templates/bilan_erell_initial.html?v=${new Date().getTime()}`);

                        const win = window.open("", "_blank");
                        if (win) {
                          win.document.write(html);
                          win.document.close();
                        } else {
                          toast.error("Le pop-up a été bloqué par le navigateur");
                        }
                      } catch (err) {
                        console.error("Error during visualization:", err);
                        toast.error("Erreur lors de la préparation de la visualisation");
                      } finally {
                        setIsVisualizing(false);
                      }
                    };

                    return (
                      <>
                        <button
                          type="button"
                          onClick={handleVisualize}
                          disabled={isVisualizing}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-70"
                        >
                          {isVisualizing ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                          Visualiser le bilan
                        </button>
                        {isDocOrDocx && (
                          <button
                            type="button"
                            onClick={handleDownloadWord}
                            disabled={isDownloadingWord}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:opacity-70"
                          >
                            {isDownloadingWord ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <FileText className="h-4 w-4" />
                            )}
                            Télécharger le bilan (Word)
                          </button>
                        )}
                      </>
                    );
                  })()}
                  <button
                    type="button"
                    onClick={saveToDatabase}
                    disabled={isSaving}
                    className={cn(
                      "inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white transition-colors",
                      savedReportId ? "bg-emerald-600 hover:bg-emerald-700" : "bg-primary-600 hover:bg-primary-700",
                      isSaving && "opacity-70 cursor-not-allowed"
                    )}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Enregistrement...
                      </>
                    ) : savedReportId ? (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        Enregistré
                      </>
                    ) : (
                      "Enregistrer le patient et le bilan"
                    )}
                  </button>
                </div>
                <p className="text-xs text-slate-500">
                  ___
                </p>
              </div>
            )}
            {phase !== "phase2" && phase !== "done" && !(orthopticAnalysis && !conclusionResult?.final) && analysisStage !== "reasoning" && analysisStage !== "conclusion" && !validatingQuestionnaire && processLog.currentStep == null && (
              <p className="text-sm text-slate-600">L'analyse et la conclusion démarrent après avoir validé le formulaire (cliquez sur "Suivant").</p>
            )}
          </div>
        )}

        {isNotPhase1 && step < 6 && (
          <div className="flex items-center justify-between pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              className={cn("inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium", step === 1 ? "text-slate-400 cursor-not-allowed" : "text-slate-700 hover:bg-slate-100")}
            >
              <ChevronLeft className="h-4 w-4" /> Précédent
            </button>
            <button
              type="button"
              onClick={() => {
                if (step === 1 && inputMode === "text" && pastedText.trim() && backgroundPhase === "idle") {
                  runPhase1FromText(pastedText, true);
                }

                // Si on est à l'étape 5 (Formulaire) et qu'on clique sur Suivant : Lancer l'analyse
                if (step === 5) {
                  setOrthopticAnalysis(null);
                  setConclusionResult(null);
                  conclusionStartedRef.current = false;
                  setAnalysisStage("reasoning");
                  // On force le reset des flags de modif car on lance l'analyse maintenant
                  patientStepModifiedRef.current = false;
                  setFormModified(false);

                  // Utiliser setTimeout pour s'assurer que tous les changements d'état du questionnaire
                  // sont bien pris en compte avant de lancer l'analyse
                  setTimeout(() => {
                    runOrthopticAnalysisOnly();
                  }, 0);
                }

                setStep((s) => Math.min(6, s + 1));
              }}
              disabled={
                (step === 1 && ((inputMode === "file" && !file) || (inputMode === "text" && !pastedText.trim())))
              }
              className={cn(
                "inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white",
                (step === 1 && ((inputMode === "file" && !file) || (inputMode === "text" && !pastedText.trim())))
                  ? "bg-slate-400 cursor-not-allowed"
                  : "bg-primary-600 hover:bg-primary-700 shadow-md"
              )}
            >
              Suivant <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Popup plus de générations gratuites */}
        {showNoQuotaModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <div className="rounded-2xl bg-white shadow-xl border border-slate-200 p-6 max-w-md w-full space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-primary-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Plus de générations gratuites</h3>
                  <p className="text-sm text-slate-600">Abonnez-vous pour continuer à générer des bilans.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowNoQuotaModal(false)}
                  className="flex-1 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Fermer
                </button>
                <a
                  href="/compte"
                  className="flex-1 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-700 text-center"
                >
                  S&apos;abonner
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal Ajout Médecin */}
      {showAddDoctorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-slate-900">Ajouter un médecin</h3>
                <button
                  onClick={handleCloseAddDoctorModal}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                  disabled={isCreatingDoctor}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Titre */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Titre (optionnel)
                  </label>
                  <input
                    type="text"
                    value={newDoctorForm.title}
                    onChange={(e) => setNewDoctorForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Dr, Pr, M., Mme..."
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:ring-2 focus:ring-primary-500"
                    disabled={isCreatingDoctor}
                  />
                </div>

                {/* Prénom */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Prénom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newDoctorForm.firstName}
                    onChange={(e) => setNewDoctorForm(prev => ({ ...prev, firstName: e.target.value }))}
                    placeholder="Jean"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:ring-2 focus:ring-primary-500"
                    disabled={isCreatingDoctor}
                    required
                  />
                </div>

                {/* Nom */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newDoctorForm.lastName}
                    onChange={(e) => setNewDoctorForm(prev => ({ ...prev, lastName: e.target.value }))}
                    placeholder="Dupont"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:ring-2 focus:ring-primary-500"
                    disabled={isCreatingDoctor}
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email (optionnel)
                  </label>
                  <input
                    type="email"
                    value={newDoctorForm.email}
                    onChange={(e) => setNewDoctorForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="jean.dupont@example.com"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:ring-2 focus:ring-primary-500"
                    disabled={isCreatingDoctor}
                  />
                </div>

                {/* Téléphone */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Téléphone (optionnel)
                  </label>
                  <input
                    type="tel"
                    value={newDoctorForm.phone}
                    onChange={(e) => setNewDoctorForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="01 23 45 67 89"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:ring-2 focus:ring-primary-500"
                    disabled={isCreatingDoctor}
                  />
                </div>

                {/* Adresse */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Adresse (optionnel)
                  </label>
                  <input
                    type="text"
                    value={newDoctorForm.address}
                    onChange={(e) => setNewDoctorForm(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="123 Rue de la Santé"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:ring-2 focus:ring-primary-500"
                    disabled={isCreatingDoctor}
                  />
                </div>

                {/* Ville et Code Postal */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Code postal (optionnel)
                    </label>
                    <input
                      type="text"
                      value={newDoctorForm.zipCode}
                      onChange={(e) => setNewDoctorForm(prev => ({ ...prev, zipCode: e.target.value }))}
                      placeholder="75001"
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:ring-2 focus:ring-primary-500"
                      disabled={isCreatingDoctor}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Ville (optionnel)
                    </label>
                    <input
                      type="text"
                      value={newDoctorForm.city}
                      onChange={(e) => setNewDoctorForm(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="Paris"
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:ring-2 focus:ring-primary-500"
                      disabled={isCreatingDoctor}
                    />
                  </div>
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleCloseAddDoctorModal}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
                  disabled={isCreatingDoctor}
                >
                  Annuler
                </button>
                <button
                  onClick={handleCreateDoctor}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  disabled={isCreatingDoctor || !newDoctorForm.firstName.trim() || !newDoctorForm.lastName.trim()}
                >
                  {isCreatingDoctor ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Création...
                    </>
                  ) : (
                    "Ajouter"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
